'use strict';

// external imports
import React, {
    useCallback,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import {keys} from 'ts-transformer-keys';
import * as d3 from 'd3';
import _ from 'lodash';
import parse from 'html-react-parser';

// prime react imports
import {Button} from 'primereact/button';
import {Card} from 'primereact/card';
import {Dialog} from 'primereact/dialog';
import {Dropdown} from 'primereact/dropdown';

// shared imports
import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import ReferenceSet from '~/shared/src/mm/ReferenceSet';
import PropertySet, {PropertyValueType} from '~/shared/src/mm/PropertySet';
import dataUtils from '~/shared/src/utilities/dataUtils';
import {dataExtraction} from '~/shared/src/utilities/dataExtraction';
import {
    ItemAttributesForCreation,
    ItemAttributesSettable
} from '~/shared/src/ci/Item';
import {ReferenceAttributesForCreation} from '~/shared/src/ci/Reference';
import {
    BaseRequestParameters,
    defaultBaseRequestParameters
} from '~/shared/src/utilities/modules/common';
import {
    buildDataDescriptor,
    DataDescriptor,
    DataObjectProperties,
    DataStructure,
    getReferenceSetsForDataObject
} from '~/shared/src/utilities/dataStructure';
import {BusinessProcess} from '~/shared/src/utilities/modules/businessProcess';
import {BusinessProcessStep} from '~/shared/src/utilities/businessProcessStep';
import {Condition} from '~/shared/src/utilities/condition';
import {convertToBoolean} from '~/shared/src/utilities/valueConverters';

// frontend imports
import {isInvalidHTML} from '~/frontend/src/utilities/valueRenderers';
import {getIntenseColorFromColorPalette} from '~/frontend/src/components/base/shared/field/valueTypeFields/ColorField';

// action imports
import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import origSetParam, {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import origSetParams, {
    SetParamsAction
} from '~/frontend/src/actions/panel/common/setParams';
import origUnsetParam, {UnsetParamAction} from '~/frontend/src/actions/panel/common/unsetParam';
import {
    clearElusiveData,
    setElusiveData as origSetElusiveData,
    SetElusiveDataAction
} from '~/frontend/src/actions/elusiveData';
import {PanelElusiveDataState} from '~/frontend/src/reducers/elusiveData';
import addItem from '~/frontend/src/actions/data/addItem';
import updateItem from '~/frontend/src/actions/data/updateItem';
import addReference from '~/frontend/src/actions/data/addReference';
import deleteItem from '~/frontend/src/actions/data/deleteItem';
import deleteReference from '~/frontend/src/actions/data/deleteReference';
import {number} from 'joi';


const debugColorScheme = {
    error            : 'F44336',
    loadFunction     : '009688',
    cacheFunction    : '607D8B',
    mouseFunction    : '0095ce',
    dragNdropFunction: 'E91E63',
    helperFunction   : 'FF5722',
    render           : '1565C0'
};


type Connection = {
    id : number;
    path : string;
    sourceStepId : number;
    targetStepId : number;
    label? : string;
    color? : string;
    startX : number;
    startY : number;
};

type DragInfo = {
    type : 'step' | 'connection';
    dragLayoutElementId : number;
    dragElement? : HTMLDivElement;
    referenceId? : number;
};

type DataDescriptors = {
    dataObject : DataStructure;
    dataDescriptors : {
        type : 'item' | 'itemMeta' | 'property' | 'propertyMeta' | 'reference' | 'referenceMeta' | 'referenceProperty' | 'referencePropertyMeta';
        dataDescriptor : DataDescriptor;
    }[];
};

export type DefaultProps = BaseRequestParameters & {
    /**
     * Additional context to be updated (elusive data)
     */
    additionalElusiveDataContexts? : string[];

    scrollTop? : number;
    scrollLeft? : number;
};
export const defaultProps : Required<DefaultProps> = {
    ...defaultBaseRequestParameters,

    additionalElusiveDataContexts: [],
    scrollTop                    : 0,
    scrollLeft                   : 0
};

export type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /*** config data ************************************************************************/
    // load from backend

    /**
     * The key of the CFG:Overview item to load the config from.
     */
    viewKey? : string;

    /**
     * A hash of the loaded overview configuration to detect changes.
     */
    configHash? : string;

    // OR provided from outside

    /**
     * The uniqueId of the component which has loaded or builds the config (to track config changes)
     */
    configUid? : string;

    /**
     * The dataContext for the form configuration to access global.data with.
     */
    configDataContext : string;

    /**
     * The id of the root config item of the form.
     */
    configRootItemId : number;

    selectedStepId? : number;
    selectedConnectionId? : number;
    onSelectStepOrConnection? : ( stepId? : number, connectionId? : number ) => void;

};
export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

export type AllProps = Required<DefaultProps> & Props;

export type ElusiveData = PanelElusiveDataState & {};

export type ReferenceType = {
    dataStructure? : DataStructure;
    addStep : () => void;
};


const setParam = origSetParam as <K extends keyof AllProps>( panelKey : string, paramKey : K, paramValue : AllProps[K] ) => SetParamAction;
const unsetParam = origUnsetParam as <K extends keyof AllProps>( panelKey : string, paramKey : K ) => UnsetParamAction;
const setParams = origSetParams as ( panelKey : string, panelProps : Partial<AllProps> ) => SetParamsAction;
const setElusiveData = origSetElusiveData as ( panelKey : string, data : ElusiveData ) => SetElusiveDataAction;


/**
 * retrieve the currently active color scheme
 */
const getColorScheme = () => matchMedia( '(prefers-color-scheme: dark)' ).matches ? 'dark' : 'light';


// TODO:
//  ----------------------------------------------------------------------------
//  implement condition
//  ----------------------------------------------------------------------------
//  add BusinessProcessStep condition - BusinessProcessStep <- Condition
//      => daraus DataChangeEventCondition (für Automationen - BackendAction) ableiten
//          - entweder RootDataObject oder verwendetes DataObject
//          - falls mehrere DataObjekte verwendet werden -> BackendAction mit entsprechenden if Abfrage und Verknüpfungen wrappen
//  ----------------------------------------------------------------------------
//    - field like using chips and brackets and AND/OR phrases
//    - create/edit in overlay
//       - chips edit overlay:
//         - use FilterField operators to valueType mapping ( AND, OR, empty, not empty, =, >, <, >=, ... )
//         - specials for:
//           - at least one referenced items (for a specified ReferenceSet) meet a condition
//           - all referenced items (for a specified ReferenceSet) meet a condition (e.g. all child tasks status equals to solved)
//         - example: ( [Task:Status(:Value) = <Value>] AND [...] ) OR [...]
//    - generate DataChangeEvent-Condition from that
//      - for used DataObject (mostly root DataObject)
//      - if more than one DataObject is used
//        - use topmost DataObject for DataChangeEvent
//        - build a wrapping if query for DataChangeEvent-BackendAction
//  ----------------------------------------------------------------------------
//  implement cypher conversion for new dataDescriptors items, properties, ...
//  ----------------------------------------------------------------------------
//  implement filter conversion for new dataDescriptors items, properties, ...
//  ----------------------------------------------------------------------------
//  implement auto actions
//  ----------------------------------------------------------------------------
//    - send an email
//    - set an attribute/property/referenceProperty
//    - reference an item (for a specified ReferenceSet)
//    - set special form for this step ( HOW? )
//    - manipulate formFields ( visibility, readOnly, required )
//  ----------------------------------------------------------------------------
//  DONE
//  ----------------------------------------------------------------------------


// TODO: next step - here we go - build leftExpression field with tiered menu for selection


const BusinessProcessDesigner = React.forwardRef<ReferenceType, AllProps>(
    ( props : AllProps, forwardedRef ) => {
        const startTime                                                         = performance.now(),
              dispatch : AppDispatch                                            = useDispatch(),
              {
                  additionalElusiveDataContexts,
                  configDataContext,
                  configRootItemId,
                  configUid,
                  onSelectStepOrConnection,
                  scrollLeft,
                  scrollTop,
                  selectedStepId,
                  selectedConnectionId,
                  uid
              }                                                                 = props,
              tempDataContext                                                   = `${uid}_temp_dataContext`,
              arrowMarkerId                                                     = `${uid}_marker_arrow`,
              shadowFilterId                                                    = `${uid}_filter_shadow`,
              user                                                              = useSelector( ( state : StoreState ) => state.session?.user ? state.session.user : undefined ),
              messageCatalogTimestamp                                           = useSelector( ( state : StoreState ) => state.messagecatalog?.timestamp ? state.messagecatalog.timestamp : 0 ),
              metaModelTimestamp                                                = useSelector( ( state : StoreState ) => state.metamodel?.timestamp ? state.metamodel.timestamp : 0 ),
              debugState                                                        = useSelector( ( state : StoreState ) => state.debugConfig?.businessProcessDesignerDebug ),
              elusiveDataConfigData                                             = useSelector( ( state : StoreState ) => configUid ? state.elusiveData?.[ configUid ]?.data : state.elusiveData?.[ uid ]?.metaData ),
              elusiveDataTempData                                               = useSelector( ( state : StoreState ) => state.elusiveData?.[ tempDataContext ]?.data ),
              additionalRenderTime                                              = useRef<number>( 0 ),
              timeoutId                                                         = useRef<NodeJS.Timeout | undefined>(),
              scrollIntervallId                                                 = useRef<NodeJS.Timeout | undefined>(),
              graphSvg                                                          = useRef<d3.Selection<SVGGElement, unknown, null, undefined> | null>( null ),
              hasRendered                                                       = useRef<boolean>( false ),
              dragInfo                                                          = useRef<DragInfo | null>( null ),
              initialRendered                                                   = useRef<boolean>( false ),
              currentScrollTop                                                  = useRef<number>( 0 ),
              currentScrollLeft                                                 = useRef<number>( 0 ),
              cBPS                                                              = useRef<BusinessProcessStep[][] | undefined>( undefined ),
              cSEB                                                              = useRef<{ [ id : number ] : DOMRect; }>( {} ),
              onBackgroundClickProps                                            = useRef<Partial<AllProps>>( {} ),
              [ colorScheme, setColorScheme ]                                   = useState<'dark' | 'light'>( getColorScheme() ),
              [ graphHeight, setGraphHeight ]                                   = useState<number>( 0 ),
              [ graphWidth, setGraphWidth ]                                     = useState<number>( 0 ),
              [ offsetElBox, setOffsetElBox ]                                   = useState<DOMRect | undefined>( undefined ),
              [ stepElementBoxes, setStepElementBoxes ]                         = useState<{ [ id : number ] : DOMRect; }>( {} ),
              [ showConditionEditorDialog, setShowConditionEditorDialog ]       = useState<boolean>( false ),
              [ stepOfConditionToEdit, setStepOfConditionToEdit ]               = useState<BusinessProcessStep | null>( null ),
              [ conditionToEdit, setConditionToEdit ]                           = useState<Condition | null>( null ),
              globalConfigDataVersions                                          = useMemo(
                  () => {
                      let retVal = JSON.stringify( elusiveDataConfigData ?? {} );

                      if ( dragInfo.current ) {
                          retVal = JSON.stringify( elusiveDataTempData ?? {} );
                      }

                      return retVal;
                  },
                  [
                      elusiveDataConfigData,
                      elusiveDataTempData
                  ]
              ),
              language                                                          = useMemo(
                  () => {
                      return user?.language ?? '';
                  },
                  [
                      user
                  ]
              ),
              debug                                                             = useMemo(
                  () => {
                      return debugState?.showAll ?? debugState?.dedicated?.[ uid ];
                  },
                  [
                      debugState
                  ]
              ),
              businessProcessStepItemTypeId                                     = useMemo(
                  () => metaModel[ 'CFG' ].getItemType( 'BusinessProcessStep' )?.id ?? 0,
                  [
                      metaModel[ 'CFG' ]
                  ]
              ),
              businessProcessStepBelongsToBusinessProcessReferenceSet           = useMemo(
                  () => metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'BusinessProcessStep', 'BusinessProcess', 'belongsTo', 'src' )?.[ 0 ],
                  [
                      metaModel[ 'CFG' ]
                  ]
              ),
              businessProcessStepBelongsToBusinessProcessReferenceSetId         = useMemo(
                  () => businessProcessStepBelongsToBusinessProcessReferenceSet?.id ?? 0,
                  [
                      businessProcessStepBelongsToBusinessProcessReferenceSet
                  ]
              ),
              businessProcessStepIsConnectedToBusinessProcessStepReferenceSet   = useMemo(
                  () => metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'BusinessProcessStep', 'BusinessProcessStep', 'isConnectedTo' )?.[ 0 ],
                  [
                      metaModel[ 'CFG' ]
                  ]
              ),
              businessProcessStepIsConnectedToBusinessProcessStepReferenceSetId = useMemo(
                  () => businessProcessStepIsConnectedToBusinessProcessStepReferenceSet?.id ?? 0,
                  [
                      businessProcessStepIsConnectedToBusinessProcessStepReferenceSet
                  ]
              ),
              businessProcessStepActivePropertySet                              = useMemo(
                  () => metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'BusinessProcessStep', 'Active' ),
                  [
                      metaModel[ 'CFG' ]
                  ]
              ),
              businessProcessStepActivePropertySetId                            = useMemo(
                  () => businessProcessStepActivePropertySet?.id ?? 0,
                  [
                      businessProcessStepActivePropertySet
                  ]
              ),
              businessProcessStepOrderPropertySet                               = useMemo(
                  () => metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'BusinessProcessStep', 'Order' ),
                  [
                      metaModel[ 'CFG' ]
                  ]
              ),
              businessProcessStepOrderPropertySetId                             = useMemo(
                  () => businessProcessStepOrderPropertySet?.id ?? 0,
                  [
                      businessProcessStepOrderPropertySet
                  ]
              ),
              orderPropertyTypeId                                               = useMemo(
                  () => metaModel[ 'CFG' ].getPropertyType( 'Order' )?.id ?? 0,
                  [
                      metaModel[ 'CFG' ]
                  ]
              ),
              orderPropertyTypeIdString                                         = useMemo(
                  () => String( orderPropertyTypeId ),
                  [
                      orderPropertyTypeId
                  ]
              ),
              businessProcessStepSubOrderPropertySet                            = useMemo(
                  () => metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'BusinessProcessStep', 'SubOrder' ),
                  [
                      metaModel[ 'CFG' ]
                  ]
              ),
              businessProcessStepSubOrderPropertySetId                          = useMemo(
                  () => businessProcessStepSubOrderPropertySet?.id ?? 0,
                  [
                      businessProcessStepSubOrderPropertySet
                  ]
              ),
              subOrderPropertyTypeId                                            = useMemo(
                  () => metaModel[ 'CFG' ].getPropertyType( 'SubOrder' )?.id ?? 0,
                  [
                      metaModel[ 'CFG' ]
                  ]
              ),
              subOrderPropertyTypeIdString                                      = useMemo(
                  () => String( subOrderPropertyTypeId ),
                  [
                      subOrderPropertyTypeId
                  ]
              ),
              businessProcessItem                                               = useMemo(
                  () => {
                      let retVal = global.data[ configDataContext ]?.items[ configRootItemId ];

                      if ( dragInfo.current ) {
                          retVal = global.data[ tempDataContext ]?.items[ configRootItemId ];
                      }

                      return retVal;
                  },
                  [
                      configDataContext,
                      globalConfigDataVersions,
                      configRootItemId
                  ]
              ),
              businessProcess                                                   = useMemo(
                  () => {
                      let retVal : BusinessProcess | undefined = undefined;

                      if ( businessProcessItem ) {
                          if ( dragInfo.current ) {
                              retVal = dataExtraction.extractBusinessProcessData( businessProcessItem, tempDataContext );
                          }
                          else {
                              retVal = dataExtraction.extractBusinessProcessData( businessProcessItem, configDataContext );
                          }
                      }

                      return retVal;
                  },
                  [
                      configDataContext,
                      globalConfigDataVersions,
                      businessProcessItem
                  ]
              ),
              businessProcessSteps                                              = useMemo(
                  () => {
                      const retVal : BusinessProcessStep[] = [];

                      if ( businessProcess?.steps ) {
                          for ( const step of businessProcess.steps ) {
                              if ( Array.isArray( step ) ) {
                                  retVal.push( ...step );
                              }
                              else {
                                  retVal.push( step );
                              }
                          }
                      }

                      return retVal;
                  },
                  [
                      businessProcess
                  ]
              ),
              dataDescriptors                                                   = useMemo(
                  () => {
                      const retVal : DataDescriptors[] = [];

                      if ( businessProcess?.dataStructure ) {
                          const dataStructure                   = businessProcess.dataStructure,
                                resolveDataDescriptorsRecursive = ( dataObject : DataStructure, parentDataObject? : DataStructure ) => {
                                    if ( !global.data[ configDataContext ].items[ dataObject.origId ].isDeleted() ) {
                                        const dataObjectSchema       = ( dataObject.item.objectSchema ?? 'data' ).toUpperCase() as Schema,
                                              dataObjectItemType     = metaModel[ dataObjectSchema ].getItemType( dataObject.item.objectType ?? '' ),
                                              dataObjectReferenceSet = parentDataObject
                                                  ? getReferenceSetsForDataObject( dataObject, dataStructure, dataObjectSchema )?.[ 0 ]
                                                  : undefined;

                                        if ( dataObjectItemType ) {
                                            const payloadHasTo1Reference          = dataObjectReferenceSet?.cardinality?.split( ':' )[ dataObjectItemType.id === dataObjectReferenceSet.srcItemTypeId ? 0 : 1 ] === '1',
                                                  retValElement : DataDescriptors = {
                                                      dataObject,
                                                      dataDescriptors: []
                                                  };

                                            let attributePathPrefixes : string[],
                                                attributePath : string,
                                                dataDescriptor : DataDescriptor | undefined,
                                                attributePathPrefixesLength : number,
                                            baseIndex : number;

                                            // reference data ( don't show for root )
                                            if ( parentDataObject && dataObjectReferenceSet ) {
                                                attributePathPrefixes = [];

                                                if ( payloadHasTo1Reference ) {
                                                    attributePathPrefixes.push( 'reference' );
                                                }
                                                else {
                                                    attributePathPrefixes.push( 'references.some' );
                                                    attributePathPrefixes.push( 'references.every' );
                                                }

                                                attributePathPrefixesLength = attributePathPrefixes.length;
                                                baseIndex = retValElement.dataDescriptors.length;

                                                for ( let index = 0; index < attributePathPrefixesLength; index++ ) {
                                                    const attributePathPrefix = attributePathPrefixes[ index ];

                                                    let count = 0;

                                                    // reference attributes - id
                                                    /* id: uncomment if needed
                                                    attributePath = `${attributePathPrefix}.id`;
                                                    dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                    if ( dataDescriptor ) {
                                                        retValElement.dataDescriptors.splice(
                                                            baseIndex + index + count++ * attributePathPrefixesLength,
                                                             0,
                                                             {
                                                                type: 'reference',
                                                                dataDescriptor
                                                            }
                                                        );
                                                    }
                                                    */

                                                    // reference meta
                                                    /* referenceTypeName: implement if needed
                                                    attributePath = `${attributePathPrefix}.referenceTypeName`;
                                                    dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                    if ( dataDescriptor ) {
                                                        retValElement.dataDescriptors.splice(
                                                            baseIndex + index + count++ * attributePathPrefixesLength,
                                                             0,
                                                             {
                                                                type: 'referenceMeta',
                                                                dataDescriptor
                                                            }
                                                        );
                                                    }
                                                    */

                                                    /* referenceSetId: implement if needed
                                                    attributePath = `${attributePathPrefix}.referenceSetId`;
                                                    dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                    if ( dataDescriptor ) {
                                                        retValElement.dataDescriptors.splice(
                                                            baseIndex + index + count++ * attributePathPrefixesLength,
                                                             0,
                                                             {
                                                                type: 'referenceMeta',
                                                                dataDescriptor
                                                            }
                                                        );
                                                    }
                                                    */

                                                    /* statusId: implement if needed
                                                    attributePath = `${attributePathPrefix}.statusId`;
                                                    dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                    if ( dataDescriptor ) {
                                                        retValElement.dataDescriptors.splice(
                                                            baseIndex + index + count++ * attributePathPrefixesLength,
                                                             0,
                                                             {
                                                                type: 'referenceMeta',
                                                                dataDescriptor
                                                            }
                                                        );
                                                    }
                                                    */

                                                    /* srcItemId: implement if needed
                                                    attributePath = `${attributePathPrefix}.srcItemId`;
                                                    dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                    if ( dataDescriptor ) {
                                                        retValElement.dataDescriptors.splice(
                                                            baseIndex + index + count++ * attributePathPrefixesLength,
                                                             0,
                                                             {
                                                                type: 'referenceMeta',
                                                                dataDescriptor
                                                            }
                                                        );
                                                    }
                                                    */

                                                    /* destItemId: implement if needed
                                                    attributePath = `${attributePathPrefix}.destItemId`;
                                                    dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                    if ( dataDescriptor ) {
                                                        retValElement.dataDescriptors.splice(
                                                            baseIndex + index + count++ * attributePathPrefixesLength,
                                                             0,
                                                             {
                                                                type: 'referenceMeta',
                                                                dataDescriptor
                                                            }
                                                        );
                                                    }
                                                    */

                                                    /* modifierUserId: implement if needed
                                                    attributePath = `${attributePathPrefix}.modifierUserId`;
                                                    dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                    if ( dataDescriptor ) {
                                                        retValElement.dataDescriptors.splice(
                                                            baseIndex + index + count++ * attributePathPrefixesLength,
                                                             0,
                                                             {
                                                                type: 'referenceMeta',
                                                                dataDescriptor
                                                            }
                                                        );
                                                    }
                                                    */

                                                    attributePath = `${attributePathPrefix}.modificationTime`;
                                                    dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                    if ( dataDescriptor ) {
                                                        retValElement.dataDescriptors.splice(
                                                            baseIndex + index + count++ * attributePathPrefixesLength,
                                                            0,
                                                            {
                                                                type: 'referenceMeta',
                                                                dataDescriptor
                                                            }
                                                        );
                                                    }

                                                    /* validatorUserId: implement if needed
                                                    attributePath = `${attributePathPrefix}.validatorUserId`;
                                                    dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                    if ( dataDescriptor ) {
                                                        retValElement.dataDescriptors.splice(
                                                            baseIndex + index + count++ * attributePathPrefixesLength,
                                                             0,
                                                             {
                                                                type: 'referenceMeta',
                                                                dataDescriptor
                                                            }
                                                        );
                                                    }
                                                    */

                                                    attributePath = `${attributePathPrefix}.validationTime`;
                                                    dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                    if ( dataDescriptor ) {
                                                        retValElement.dataDescriptors.splice(
                                                            baseIndex + index + count++ * attributePathPrefixesLength,
                                                            0,
                                                            {
                                                                type: 'referenceMeta',
                                                                dataDescriptor
                                                            }
                                                        );
                                                    }
                                                }


                                                // reference properties - get possible fields for all properties
                                                const referencePropertySets = metaModel[ dataObjectSchema ].getReferencePropertySetsForReferenceSet( dataObjectReferenceSet.id );

                                                referencePropertySets.sort(
                                                    ( pset1, pset2 ) => pset1.label.toLowerCase() > pset2.label.toLowerCase() ? 1 : ( pset1.label.toLowerCase() < pset2.label.toLowerCase() ? -1 : 0 )
                                                );

                                                attributePathPrefixes = [];

                                                if ( payloadHasTo1Reference ) {
                                                    attributePathPrefixes.push( 'referenceProperty' );
                                                }
                                                else {
                                                    attributePathPrefixes.push( 'referenceProperties.some' );
                                                    attributePathPrefixes.push( 'referenceProperties.every' );
                                                }

                                                attributePathPrefixesLength = attributePathPrefixes.length;
                                                baseIndex = retValElement.dataDescriptors.length;

                                                for ( let index = 0; index < attributePathPrefixesLength; index++ ) {
                                                    const attributePathPrefix = attributePathPrefixes[ index ];

                                                    let count = 0;

                                                    for ( const referencePropertySet of referencePropertySets ) {

                                                        // property attributes - key, value
                                                        if ( referencePropertySet.isEnumerated && referencePropertySet.isKeySettable ) {
                                                            attributePath = `${attributePathPrefix}.${referencePropertySet.name}.key`;
                                                            dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                            if ( dataDescriptor ) {
                                                                retValElement.dataDescriptors.splice(
                                                                    baseIndex + index + count++ * attributePathPrefixesLength,
                                                                    0,
                                                                    {
                                                                        type: 'referenceProperty',
                                                                        dataDescriptor
                                                                    }
                                                                );
                                                            }
                                                        }

                                                        if ( !referencePropertySet.isEnumerated || referencePropertySet.isValueSettable ) {
                                                            attributePath = `${attributePathPrefix}.${referencePropertySet.name}.value`;
                                                            dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                            if ( dataDescriptor ) {
                                                                retValElement.dataDescriptors.splice(
                                                                    baseIndex + index + count++ * attributePathPrefixesLength,
                                                                    0,
                                                                    {
                                                                        type: 'referenceProperty',
                                                                        dataDescriptor
                                                                    }
                                                                );
                                                            }
                                                        }


                                                        // reference property meta (non enumerated only)
                                                        if ( !referencePropertySet.isEnumerated ) {
                                                            /* propertyTypeName: implement if needed
                                                            attributePath = `${attributePathPrefix}.${referencePropertySet.name}.propertyTypeName`;
                                                            dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                            if ( dataDescriptor ) {
                                                                retValElement.dataDescriptors.splice(
                                                                    baseIndex + index + count++ * attributePathPrefixesLength,
                                                                    0,
                                                                    {
                                                                        type: 'referencePropertyMeta',
                                                                        dataDescriptor
                                                                    }
                                                                );
                                                            }
                                                            */

                                                            /* referencePropertySetId: implement if needed
                                                            attributePath = `${attributePathPrefix}.${referencePropertySet.name}.referencePropertySetId`;
                                                            dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                            if ( dataDescriptor ) {
                                                                retValElement.dataDescriptors.splice(
                                                                    baseIndex + index + count++ * attributePathPrefixesLength,
                                                                    0,
                                                                    {
                                                                        type: 'referencePropertyMeta',
                                                                        dataDescriptor
                                                                    }
                                                                );
                                                            }
                                                            */

                                                            /* statusId: implement if needed
                                                            attributePath = `${attributePathPrefix}.${referencePropertySet.name}.statusId`;
                                                            dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                            if ( dataDescriptor ) {
                                                                retValElement.dataDescriptors.splice(
                                                                    baseIndex + index + count++ * attributePathPrefixesLength,
                                                                    0,
                                                                    {
                                                                        type: 'referencePropertyMeta',
                                                                        dataDescriptor
                                                                    }
                                                                );
                                                            }
                                                            */

                                                            /* modifierUserId: implement if needed
                                                            attributePath = `${attributePathPrefix}.${referencePropertySet.name}.modifierUserId`;
                                                            dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                            if ( dataDescriptor ) {
                                                                retValElement.dataDescriptors.splice(
                                                                    baseIndex + index + count++ * attributePathPrefixesLength,
                                                                    0,
                                                                    {
                                                                        type: 'referencePropertyMeta',
                                                                        dataDescriptor
                                                                    }
                                                                );
                                                            }
                                                            */

                                                            attributePath = `${attributePathPrefix}.${referencePropertySet.name}.modificationTime`;
                                                            dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                            if ( dataDescriptor ) {
                                                                retValElement.dataDescriptors.splice(
                                                                    baseIndex + index + count++ * attributePathPrefixesLength,
                                                                    0,
                                                                    {
                                                                        type: 'referencePropertyMeta',
                                                                        dataDescriptor
                                                                    }
                                                                );
                                                            }

                                                            /* validatorUserId: implement if needed
                                                            attributePath = `${attributePathPrefix}.${referencePropertySet.name}.validatorUserId`;
                                                            dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                            if ( dataDescriptor ) {
                                                                retValElement.dataDescriptors.splice(
                                                                    baseIndex + index + count++ * attributePathPrefixesLength,
                                                                    0,
                                                                    {
                                                                        type: 'referencePropertyMeta',
                                                                        dataDescriptor
                                                                    }
                                                                );
                                                            }
                                                            */

                                                            attributePath = `${attributePathPrefix}.${referencePropertySet.name}.validationTime`;
                                                            dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                            if ( dataDescriptor ) {
                                                                retValElement.dataDescriptors.splice(
                                                                    baseIndex + index + count++ * attributePathPrefixesLength,
                                                                    0,
                                                                    {
                                                                        type: 'referencePropertyMeta',
                                                                        dataDescriptor
                                                                    }
                                                                );
                                                            }
                                                        }
                                                    }
                                                }
                                            }

                                            // item data
                                            attributePathPrefixes = [];

                                            if ( !parentDataObject || payloadHasTo1Reference ) {
                                                attributePathPrefixes.push( 'item' );
                                            }
                                            else {
                                                attributePathPrefixes.push( 'items.some' );
                                                attributePathPrefixes.push( 'items.every' );
                                            }

                                            attributePathPrefixesLength = attributePathPrefixes.length;
                                            baseIndex = retValElement.dataDescriptors.length;

                                            for ( let index = 0; index < attributePathPrefixesLength; index++ ) {
                                                const attributePathPrefix = attributePathPrefixes[ index ];

                                                let count = 0;

                                                // item attributes - id, key, name
                                                /* id: uncomment if needed
                                                attributePath = `${attributePathPrefix}.id`;
                                                dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                if ( dataDescriptor ) {
                                                    retValElement.dataDescriptors.splice(
                                                        baseIndex + index + count++ * attributePathPrefixesLength,
                                                        0,
                                                        {
                                                            type: 'item',
                                                            dataDescriptor
                                                        }
                                                    );
                                                }
                                                */

                                                attributePath = `${attributePathPrefix}.key`;
                                                dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                if ( dataDescriptor ) {
                                                    retValElement.dataDescriptors.splice(
                                                        baseIndex + index + count++ * attributePathPrefixesLength,
                                                        0,
                                                        {
                                                            type: 'item',
                                                            dataDescriptor
                                                        }
                                                    );
                                                }

                                                attributePath = `${attributePathPrefix}.name`;
                                                dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                if ( dataDescriptor ) {
                                                    retValElement.dataDescriptors.splice(
                                                        baseIndex + index + count++ * attributePathPrefixesLength,
                                                        0,
                                                        {
                                                            type: 'item',
                                                            dataDescriptor
                                                        }
                                                    );
                                                }


                                                // item meta
                                                /* itemTypeName: uncomment if needed
                                                attributePath = `${attributePathPrefix}.itemTypeName`;
                                                dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                if ( dataDescriptor ) {
                                                    retValElement.dataDescriptors.splice(
                                                        baseIndex + index + count++ * attributePathPrefixesLength,
                                                        0,
                                                        {
                                                            type: 'itemMeta',
                                                            dataDescriptor
                                                        }
                                                    );
                                                }
                                                */

                                                /* itemTypeId: uncomment if needed
                                                attributePath = `${attributePathPrefix}.itemTypeId`;
                                                dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                if ( dataDescriptor ) {
                                                    retValElement.dataDescriptors.splice(
                                                        baseIndex + index + count++ * attributePathPrefixesLength,
                                                        0,
                                                        {
                                                            type: 'itemMeta',
                                                            dataDescriptor
                                                        }
                                                    );
                                                }
                                                */

                                                /* statusId: uncomment if needed
                                                attributePath = `${attributePathPrefix}.statusId`;
                                                dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                if ( dataDescriptor ) {
                                                    retValElement.dataDescriptors.splice(
                                                        baseIndex + index + count++ * attributePathPrefixesLength,
                                                        0,
                                                        {
                                                            type: 'itemMeta',
                                                            dataDescriptor
                                                        }
                                                    );
                                                }
                                                */

                                                /* creatorUserId: uncomment if needed
                                                attributePath = `${attributePathPrefix}.creatorUserId`;
                                                dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                if ( dataDescriptor ) {
                                                    retValElement.dataDescriptors.splice(
                                                        baseIndex + index + count++ * attributePathPrefixesLength,
                                                        0,
                                                        {
                                                            type: 'itemMeta',
                                                            dataDescriptor
                                                        }
                                                    );
                                                }
                                                */

                                                attributePath = `${attributePathPrefix}.creationTime`;
                                                dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                if ( dataDescriptor ) {
                                                    retValElement.dataDescriptors.splice(
                                                        baseIndex + index + count++ * attributePathPrefixesLength,
                                                        0,
                                                        {
                                                            type: 'itemMeta',
                                                            dataDescriptor
                                                        }
                                                    );
                                                }

                                                /* modifierUserId: uncomment if needed
                                                attributePath = `${attributePathPrefix}.modifierUserId`;
                                                dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                if ( dataDescriptor ) {
                                                    retValElement.dataDescriptors.splice(
                                                        baseIndex + index + count++ * attributePathPrefixesLength,
                                                        0,
                                                        {
                                                            type: 'itemMeta',
                                                            dataDescriptor
                                                        }
                                                    );
                                                }
                                                */

                                                attributePath = `${attributePathPrefix}.modificationTime`;
                                                dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                if ( dataDescriptor ) {
                                                    retValElement.dataDescriptors.splice(
                                                        baseIndex + index + count++ * attributePathPrefixesLength,
                                                        0,
                                                        {
                                                            type: 'itemMeta',
                                                            dataDescriptor
                                                        }
                                                    );
                                                }

                                                /* validatorUserId: uncomment if needed
                                                attributePath = `${attributePathPrefix}.validatorUserId`;
                                                dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                if ( dataDescriptor ) {
                                                    retValElement.dataDescriptors.splice(
                                                        baseIndex + index + count++ * attributePathPrefixesLength,
                                                        0,
                                                        {
                                                            type: 'itemMeta',
                                                            dataDescriptor
                                                        }
                                                    );
                                                }
                                                */

                                                attributePath = `${attributePathPrefix}.validationTime`;
                                                dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                if ( dataDescriptor ) {
                                                    retValElement.dataDescriptors.splice(
                                                        baseIndex + index + count++ * attributePathPrefixesLength,
                                                        0,
                                                        {
                                                            type: 'itemMeta',
                                                            dataDescriptor
                                                        }
                                                    );
                                                }
                                            }

                                            // item properties - get possible fields for all properties
                                            const propertySets = metaModel[ dataObjectSchema ].getPropertySetsForItemType( dataObjectItemType.id );

                                            propertySets.sort(
                                                ( pset1, pset2 ) => pset1.label.toLowerCase() > pset2.label.toLowerCase() ? 1 : ( pset1.label.toLowerCase() < pset2.label.toLowerCase() ? -1 : 0 )
                                            );

                                            attributePathPrefixes = [];

                                            if ( !parentDataObject || payloadHasTo1Reference ) {
                                                attributePathPrefixes.push( 'property' );
                                            }
                                            else {
                                                attributePathPrefixes.push( 'properties.some' );
                                                attributePathPrefixes.push( 'properties.every' );
                                            }

                                            attributePathPrefixesLength = attributePathPrefixes.length;
                                            baseIndex = retValElement.dataDescriptors.length;

                                            for ( let index = 0; index < attributePathPrefixesLength; index++ ) {
                                                const attributePathPrefix = attributePathPrefixes[ index ];

                                                let count = 0;

                                                for ( const propertySet of propertySets ) {

                                                    // property attributes - key, value
                                                    if ( propertySet.isEnumerated && propertySet.isKeySettable ) {
                                                        attributePath = `${attributePathPrefix}.${propertySet.name}.key`;
                                                        dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                        if ( dataDescriptor ) {
                                                            retValElement.dataDescriptors.splice(
                                                                baseIndex + index + count++ * attributePathPrefixesLength,
                                                                0,
                                                                {
                                                                    type: 'property',
                                                                    dataDescriptor
                                                                }
                                                            );
                                                        }
                                                    }

                                                    if ( !propertySet.isEnumerated || propertySet.isValueSettable ) {
                                                        attributePath = `${attributePathPrefix}.${propertySet.name}.value`;
                                                        dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                        if ( dataDescriptor ) {
                                                            retValElement.dataDescriptors.splice(
                                                                baseIndex + index + count++ * attributePathPrefixesLength,
                                                                0,
                                                                {
                                                                    type: 'property',
                                                                    dataDescriptor
                                                                }
                                                            );
                                                        }
                                                    }


                                                    // property meta (non enumerated only)
                                                    if ( !propertySet.isEnumerated ) {
                                                        /* propertyTypeName: implement if needed
                                                        attributePath = `${attributePathPrefix}.${propertySet.name}.propertyTypeName`;
                                                        dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                        if ( dataDescriptor ) {
                                                            retValElement.dataDescriptors.splice(
                                                                baseIndex + index + count++ * attributePathPrefixesLength,
                                                                0,
                                                                {
                                                                    type: 'propertyMeta',
                                                                    dataDescriptor
                                                                }
                                                            );
                                                        }
                                                        */

                                                        /* propertySetId: implement if needed
                                                        attributePath = `${attributePathPrefix}.${propertySet.name}.propertySetId`;
                                                        dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                        if ( dataDescriptor ) {
                                                            retValElement.dataDescriptors.splice(
                                                                baseIndex + index + count++ * attributePathPrefixesLength,
                                                                0,
                                                                {
                                                                    type: 'propertyMeta',
                                                                    dataDescriptor
                                                                }
                                                            );
                                                        }
                                                        */

                                                        /* statusId: implement if needed
                                                        attributePath = `${attributePathPrefix}.${propertySet.name}.statusId`;
                                                        dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                        if ( dataDescriptor ) {
                                                            retValElement.dataDescriptors.splice(
                                                                baseIndex + index + count++ * attributePathPrefixesLength,
                                                                0,
                                                                {
                                                                    type: 'propertyMeta',
                                                                    dataDescriptor
                                                                }
                                                            );
                                                        }
                                                        */

                                                        /* modifierUserId: implement if needed
                                                        attributePath = `${attributePathPrefix}.${propertySet.name}.modifierUserId`;
                                                        dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                        if ( dataDescriptor ) {
                                                            retValElement.dataDescriptors.splice(
                                                                baseIndex + index + count++ * attributePathPrefixesLength,
                                                                0,
                                                                {
                                                                    type: 'propertyMeta',
                                                                    dataDescriptor
                                                                }
                                                            );
                                                        }
                                                        */

                                                        attributePath = `${attributePathPrefix}.${propertySet.name}.modificationTime`;
                                                        dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                        if ( dataDescriptor ) {
                                                            retValElement.dataDescriptors.splice(
                                                                baseIndex + index + count++ * attributePathPrefixesLength,
                                                                0,
                                                                {
                                                                    type: 'propertyMeta',
                                                                    dataDescriptor
                                                                }
                                                            );
                                                        }

                                                        /* validatorUserId: implement if needed
                                                        attributePath = `${attributePathPrefix}.${propertySet.name}.validatorUserId`;
                                                        dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                        if ( dataDescriptor ) {
                                                            retValElement.dataDescriptors.splice(
                                                                baseIndex + index + count++ * attributePathPrefixesLength,
                                                                0,
                                                                {
                                                                    type: 'propertyMeta',
                                                                    dataDescriptor
                                                                }
                                                            );
                                                        }
                                                        */

                                                        attributePath = `${attributePathPrefix}.${propertySet.name}.validationTime`;
                                                        dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObject.item } as unknown as DataObjectProperties );

                                                        if ( dataDescriptor ) {
                                                            retValElement.dataDescriptors.splice(
                                                                baseIndex + index + count++ * attributePathPrefixesLength,
                                                                0,
                                                                {
                                                                    type: 'propertyMeta',
                                                                    dataDescriptor
                                                                }
                                                            );
                                                        }
                                                    }
                                                }
                                            }

                                            if ( retValElement.dataDescriptors.length ) {
                                                retVal.push( retValElement );
                                            }
                                        }

                                        if ( dataObject.dataStructure?.length ) {
                                            for ( const subDataObject of dataObject.dataStructure ) {
                                                resolveDataDescriptorsRecursive( subDataObject, dataObject );
                                            }
                                        }
                                    }
                                };

                          resolveDataDescriptorsRecursive( businessProcess.dataStructure );
                      }

                      return retVal;
                  },
                  [
                      businessProcess?.dataStructure,
                      configDataContext
                  ]
              ),
              setScrollPosition                                                 = () => {
                  const scrollElement = document.getElementById( `${uid}_businessProcess` )?.parentElement;

                  if ( scrollElement ) {
                      scrollElement.scrollLeft = currentScrollLeft.current;
                      scrollElement.scrollTop = currentScrollTop.current;
                  }
              },
              getScrollPosition                                                 = () => {
                  const scrollElement = document.getElementById( `${uid}_businessProcess` )?.parentElement;

                  if ( scrollElement ) {
                      dispatch( setParams( uid, {
                          scrollTop : scrollElement.scrollTop ?? 0,
                          scrollLeft: scrollElement.scrollLeft ?? 0
                      } ) );
                  }
              },
              debouncedGetScrollPosition                                        = _.debounce( getScrollPosition.bind( null ), 250 ),
              onStepDragMouseMove                                               = useCallback(
                  ( event : MouseEvent ) => {
                      let endUp : boolean = false;

                      if ( dragInfo.current ) {
                          // set position of drag ghost
                          if ( dragInfo.current.dragElement ) {
                              dragInfo.current.dragElement.style.left = ( event.pageX - 50 ) + 'px';
                              dragInfo.current.dragElement.style.top = ( event.pageY - 50 ) + 'px';
                          }

                          // scroll form - end up on scrolling
                          const scrollElement    = document.getElementById( `${uid}_businessProcess` )?.parentElement,
                                scrollElementBox = scrollElement?.getBoundingClientRect();

                          if ( scrollIntervallId.current ) {
                              clearInterval( scrollIntervallId.current );
                          }

                          if ( scrollElement && scrollElementBox ) {
                              if ( scrollElement.scrollHeight > scrollElement.clientHeight || scrollElement.scrollWidth > scrollElement.clientWidth ) {
                                  if (
                                      event.pageX > scrollElementBox.x && event.pageX < ( scrollElementBox.x + scrollElementBox.width )
                                      && event.pageY < ( scrollElementBox.y + .1 * scrollElement.clientHeight ) && currentScrollTop.current > 1
                                  ) {
                                      if ( debug ) {
                                          console.log( '    -> scroll form up' );
                                      }

                                      dispatch( setParam( uid, 'scrollTop', Math.max( 0, currentScrollTop.current - 25 ) ) );

                                      scrollIntervallId.current = setInterval(
                                          () => {
                                              dispatch( setParam( uid, 'scrollTop', Math.max( 0, currentScrollTop.current - 25 ) ) );
                                          },
                                          25
                                      );

                                      endUp = true;
                                  }
                                  else if (
                                      event.pageX > scrollElementBox.x && event.pageX < ( scrollElementBox.x + scrollElementBox.width )
                                      && event.pageY > ( scrollElementBox.y + .9 * scrollElement.clientHeight ) && currentScrollTop.current < ( scrollElement.scrollHeight - scrollElement.clientHeight ) - 1
                                  ) {
                                      if ( debug ) {
                                          console.log( '    -> scroll form down' );
                                      }

                                      dispatch( setParam( uid, 'scrollTop', Math.min( scrollElement.scrollHeight - scrollElement.clientHeight, currentScrollTop.current + 25 ) ) );

                                      scrollIntervallId.current = setInterval(
                                          () => {
                                              dispatch( setParam( uid, 'scrollTop', Math.min( scrollElement.scrollHeight - scrollElement.clientHeight, currentScrollTop.current + 25 ) ) );
                                          },
                                          25
                                      );

                                      endUp = true;
                                  }
                                  if (
                                      event.pageY > scrollElementBox.y && event.pageY < ( scrollElementBox.y + scrollElementBox.height )
                                      && event.pageX < ( scrollElementBox.x + .1 * scrollElement.clientWidth ) && currentScrollLeft.current > 1
                                  ) {
                                      if ( debug ) {
                                          console.log( '    -> scroll form left' );
                                      }

                                      dispatch( setParam( uid, 'scrollLeft', Math.max( 0, currentScrollLeft.current - 25 ) ) );

                                      scrollIntervallId.current = setInterval(
                                          () => {
                                              dispatch( setParam( uid, 'scrollLeft', Math.max( 0, currentScrollLeft.current - 25 ) ) );
                                          },
                                          25
                                      );

                                      endUp = true;
                                  }
                                  else if (
                                      event.pageY > scrollElementBox.y && event.pageY < ( scrollElementBox.y + scrollElementBox.height )
                                      && event.pageX > ( scrollElementBox.x + .9 * scrollElement.clientWidth ) && currentScrollLeft.current < ( scrollElement.scrollWidth - scrollElement.clientWidth ) - 1
                                  ) {
                                      if ( debug ) {
                                          console.log( '    -> scroll form right' );
                                      }

                                      dispatch( setParam( uid, 'scrollLeft', Math.min( scrollElement.scrollWidth - scrollElement.clientWidth, currentScrollLeft.current + 25 ) ) );

                                      scrollIntervallId.current = setInterval(
                                          () => {
                                              dispatch( setParam( uid, 'scrollLeft', Math.min( scrollElement.scrollWidth - scrollElement.clientWidth, currentScrollLeft.current + 25 ) ) );
                                          },
                                          25
                                      );

                                      endUp = true;
                                  }
                              }
                          }

                      }

                      // compute new order and subOrder
                      if ( !endUp && dragInfo.current && cBPS.current && cSEB.current ) {
                          let newOrder : number    = 0,
                              newSubOrder : number = 1,
                              push : boolean       = false;

                          // get new order
                          for ( let stepIndex = 0; stepIndex < cBPS.current.length; stepIndex++ ) {
                              const box = cSEB.current[ cBPS.current[ stepIndex ][ 0 ].data.id ];

                              if ( box ) {
                                  if ( event.x < box.x + box.width ) {
                                      newOrder = stepIndex + 1;

                                      if ( event.x < box.x ) {
                                          push = true;
                                      }

                                      break;
                                  }
                                  else if ( stepIndex === cBPS.current.length - 1 && event.x > box.x + box.width ) {
                                      newOrder = cBPS.current.length + 1;
                                      push = true;
                                  }
                              }
                          }
                          // get new subOrder
                          if ( !push ) {
                              for ( let subStepIndex = 0; subStepIndex < cBPS.current[ newOrder - 1 ].length; subStepIndex++ ) {
                                  const box = cSEB.current[ cBPS.current[ newOrder - 1 ][ subStepIndex ].data.id ];

                                  if ( box ) {
                                      if ( event.y < box.y + box.height ) {
                                          newSubOrder = subStepIndex + 1;

                                          break;
                                      }
                                      else if ( subStepIndex === cBPS.current[ newOrder - 1 ].length - 1 && event.y > box.y + box.height ) {
                                          newSubOrder = cBPS.current[ newOrder - 1 ].length + 1;
                                      }
                                  }
                              }
                          }

                          // reorder
                          if ( newOrder !== undefined ) {
                              for ( let stepIndex = 0; stepIndex < cBPS.current.length; stepIndex++ ) {
                                  const stepFound = cBPS.current[ stepIndex ].find( step => step.data.id === dragInfo.current!.dragLayoutElementId );

                                  if ( stepFound && cBPS.current[ stepIndex ].length === 1 ) {
                                      push = false;
                                  }

                                  for ( let subStepIndex = 0; subStepIndex < cBPS.current[ stepIndex ].length; subStepIndex++ ) {
                                      const subStep = cBPS.current[ stepIndex ][ subStepIndex ];

                                      let orderValue    = stepIndex + 1,
                                          subOrderValue = subStepIndex + 1;

                                      if ( subStep.data.id === dragInfo.current.dragLayoutElementId ) {
                                          orderValue = newOrder;
                                      }
                                      else if ( push && orderValue >= newOrder ) {
                                          orderValue++;
                                      }

                                      if ( subStep.data.id === dragInfo.current.dragLayoutElementId ) {
                                          subOrderValue = newSubOrder;
                                      }
                                      else if ( subOrderValue >= newSubOrder ) {
                                          subOrderValue++;
                                      }

                                      dispatch( updateItem(
                                          tempDataContext,
                                          subStep.data.id,
                                          {
                                              properties: {
                                                  [ orderPropertyTypeIdString ]   : {
                                                      value: orderValue
                                                  },
                                                  [ subOrderPropertyTypeIdString ]: {
                                                      value: subOrderValue
                                                  }
                                              }
                                          }
                                      ) );
                                  }
                              }
                          }
                      }
                  },
                  [
                      tempDataContext,
                      uid
                  ]
              ),
              onStepDragMouseUp                                                 = useCallback(
                  ( event : MouseEvent ) => {

                      // remove listeners for drag control
                      document.querySelector( 'html' )?.removeEventListener( 'mouseup', onStepDragMouseUp );
                      document.querySelector( 'html' )?.removeEventListener( 'mousemove', onStepDragMouseMove );

                      // apply changes to global.data[ tempDataContext ].items to global.data[ configDataContext ].items
                      for ( const item of Object.values( global.data[ tempDataContext ]?.items ?? {} ) ) {
                          const orderProperty    = item.properties.get( orderPropertyTypeId ),
                                subOrderProperty = item.properties.get( subOrderPropertyTypeId );

                          if ( item.isModified() ) {
                              let properties : ItemAttributesSettable['properties'] | undefined = undefined;

                              if ( orderProperty && orderProperty.isModified() ) {
                                  properties = {
                                      ...( properties ?? {} ),
                                      [ orderPropertyTypeIdString ]: {
                                          value: orderProperty.value
                                      }
                                  }
                              }

                              if ( subOrderProperty && subOrderProperty.isModified() ) {
                                  properties = {
                                      ...( properties ?? {} ),
                                      [ subOrderPropertyTypeIdString ]: {
                                          value: subOrderProperty.value
                                      }
                                  }
                              }

                              if ( properties !== undefined ) {
                                  dispatch( updateItem(
                                      configDataContext,
                                      item.id,
                                      {
                                          properties
                                      },
                                      additionalElusiveDataContexts
                                  ) );
                              }
                          }
                      }

                      // clean temp dataContext
                      delete global.data[ tempDataContext ];
                      clearElusiveData( tempDataContext );

                      // unmark dragged step element
                      document.getElementById( `${uid}_businessProcessStep_${dragInfo.current?.dragLayoutElementId}` )?.classList.remove( 'yn-step-dragged' );

                      // destroy drag ghost
                      if ( dragInfo.current?.dragElement ) {
                          document.body.removeChild( dragInfo.current.dragElement );
                      }

                      // clean drag info
                      dragInfo.current = null;
                  },
                  [
                      additionalElusiveDataContexts,
                      configDataContext,
                      onStepDragMouseMove,
                      tempDataContext,
                      uid
                  ]
              ),
              onStepDragStart                                                   = useCallback(
                  ( event : any, step : BusinessProcessStep ) => {
                      const stepPreview = document.createElement( 'div' ),
                            title       = step.messages?.title && typeof step.messages.title === 'string'
                                ? __( step.messages.title )
                                : step.data.name;

                      event.preventDefault();
                      event.stopPropagation();
                      event.nativeEvent.preventDefault();

                      // add listeners for drag control
                      document.querySelector( 'html' )?.addEventListener( 'mousemove', onStepDragMouseMove );
                      document.querySelector( 'html' )?.addEventListener( 'mouseup', onStepDragMouseUp );

                      // init temp dataContext
                      for ( const item of Object.values( global.data[ configDataContext ]?.items ?? {} ) ) {
                          item.clone( undefined, undefined, tempDataContext ).commit();
                      }
                      for ( const reference of Object.values( global.data[ configDataContext ]?.references ?? {} ) ) {
                          reference.clone( undefined, undefined, tempDataContext ).commit();
                      }

                      // init drag info
                      dragInfo.current = {
                          type               : 'step',
                          dragLayoutElementId: step.data.id
                      };

                      // mark dragged the step element
                      document.getElementById( `${uid}_businessProcessStep_${step.data.id}` )?.classList.add( 'yn-step-dragged' );

                      // create drag ghost
                      dragInfo.current.dragElement = document.createElement( 'div' );
                      dragInfo.current.dragElement.id = 'drag';
                      dragInfo.current.dragElement.className = 'yn-business-step-drag-proxy';
                      dragInfo.current.dragElement.style.left = ( event.pageX - 50 ) + 'px';
                      dragInfo.current.dragElement.style.top = ( event.pageY - 50 ) + 'px';
                      document.body.appendChild( dragInfo.current.dragElement );

                      stepPreview.id = 'drag_preview';
                      stepPreview.className = 'yn-business-step-drag-preview';
                      stepPreview.innerHTML = title!;
                      dragInfo.current.dragElement.appendChild( stepPreview );
                  },
                  [
                      configDataContext,
                      onStepDragMouseMove,
                      onStepDragMouseUp,
                      tempDataContext,
                      uid
                  ]
              ),
              onStepClick                                                       = useCallback(
                  ( event : any, step : BusinessProcessStep ) => {
                      onSelectStepOrConnection?.( step.data.id, undefined );
                  },
                  [
                      onSelectStepOrConnection,
                      uid
                  ]
              ),
              onStepOver                                                        = useCallback(
                  ( event : any, step : BusinessProcessStep ) => {

                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onStepOut                                                         = useCallback(
                  ( event : any, step : BusinessProcessStep ) => {

                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onStepDeleteClick                                                 = useCallback(
                  ( event : any, step : BusinessProcessStep ) => {
                      const references = dataUtils.findReferences( configDataContext, undefined, step.data.id );

                      dispatch( deleteItem(
                          configDataContext,
                          step.data.id,
                          true,
                          additionalElusiveDataContexts
                      ) );

                      for ( const reference of references ?? [] ) {
                          dispatch( deleteReference(
                              configDataContext,
                              reference.id,
                              true,
                              additionalElusiveDataContexts
                          ) );
                      }

                      onSelectStepOrConnection?.( configRootItemId, undefined );
                  },
                  [
                      additionalElusiveDataContexts,
                      configDataContext,
                      configRootItemId,
                      onSelectStepOrConnection
                  ]
              ),
              onConnectionDragMouseMove                                         = useCallback(
                  ( event : MouseEvent ) => {
                      let endUp : boolean = false;

                      if ( dragInfo.current ) {

                          // scroll form - end up on scrolling
                          const scrollElement    = document.getElementById( `${uid}_businessProcess` )?.parentElement,
                                scrollElementBox = scrollElement?.getBoundingClientRect();

                          if ( scrollIntervallId.current ) {
                              clearInterval( scrollIntervallId.current );
                          }

                          if ( scrollElement && scrollElementBox ) {
                              if ( scrollElement.scrollHeight > scrollElement.clientHeight || scrollElement.scrollWidth > scrollElement.clientWidth ) {
                                  if (
                                      event.pageX > scrollElementBox.x && event.pageX < ( scrollElementBox.x + scrollElementBox.width )
                                      && event.pageY < ( scrollElementBox.y + .1 * scrollElement.clientHeight ) && currentScrollTop.current > 1
                                  ) {
                                      if ( debug ) {
                                          console.log( '    -> scroll form up' );
                                      }

                                      dispatch( setParam( uid, 'scrollTop', Math.max( 0, currentScrollTop.current - 25 ) ) );

                                      scrollIntervallId.current = setInterval(
                                          () => {
                                              dispatch( setParam( uid, 'scrollTop', Math.max( 0, currentScrollTop.current - 25 ) ) );
                                          },
                                          25
                                      );

                                      endUp = true;
                                  }
                                  else if (
                                      event.pageX > scrollElementBox.x && event.pageX < ( scrollElementBox.x + scrollElementBox.width )
                                      && event.pageY > ( scrollElementBox.y + .9 * scrollElement.clientHeight ) && currentScrollTop.current < ( scrollElement.scrollHeight - scrollElement.clientHeight ) - 1
                                  ) {
                                      if ( debug ) {
                                          console.log( '    -> scroll form down' );
                                      }

                                      dispatch( setParam( uid, 'scrollTop', Math.min( scrollElement.scrollHeight - scrollElement.clientHeight, currentScrollTop.current + 25 ) ) );

                                      scrollIntervallId.current = setInterval(
                                          () => {
                                              dispatch( setParam( uid, 'scrollTop', Math.min( scrollElement.scrollHeight - scrollElement.clientHeight, currentScrollTop.current + 25 ) ) );
                                          },
                                          25
                                      );

                                      endUp = true;
                                  }
                                  if (
                                      event.pageY > scrollElementBox.y && event.pageY < ( scrollElementBox.y + scrollElementBox.height )
                                      && event.pageX < ( scrollElementBox.x + .1 * scrollElement.clientWidth ) && currentScrollLeft.current > 1
                                  ) {
                                      if ( debug ) {
                                          console.log( '    -> scroll form left' );
                                      }

                                      dispatch( setParam( uid, 'scrollLeft', Math.max( 0, currentScrollLeft.current - 25 ) ) );

                                      scrollIntervallId.current = setInterval(
                                          () => {
                                              dispatch( setParam( uid, 'scrollLeft', Math.max( 0, currentScrollLeft.current - 25 ) ) );
                                          },
                                          25
                                      );

                                      endUp = true;
                                  }
                                  else if (
                                      event.pageY > scrollElementBox.y && event.pageY < ( scrollElementBox.y + scrollElementBox.height )
                                      && event.pageX > ( scrollElementBox.x + .9 * scrollElement.clientWidth ) && currentScrollLeft.current < ( scrollElement.scrollWidth - scrollElement.clientWidth ) - 1
                                  ) {
                                      if ( debug ) {
                                          console.log( '    -> scroll form right' );
                                      }

                                      dispatch( setParam( uid, 'scrollLeft', Math.min( scrollElement.scrollWidth - scrollElement.clientWidth, currentScrollLeft.current + 25 ) ) );

                                      scrollIntervallId.current = setInterval(
                                          () => {
                                              dispatch( setParam( uid, 'scrollLeft', Math.min( scrollElement.scrollWidth - scrollElement.clientWidth, currentScrollLeft.current + 25 ) ) );
                                          },
                                          25
                                      );

                                      endUp = true;
                                  }
                              }
                          }
                      }

                      if ( !endUp ) {
                          // do nothing
                      }
                  },
                  [
                      uid
                  ]
              ),
              onConnectionDragEnd                                               = useCallback(
                  ( event : MouseEvent ) => {
                      const elements = document.querySelectorAll( '.yn-businessprocess-step-substep' );

                      // remove listeners for drag control
                      document.querySelector( 'html' )?.removeEventListener( 'mousemove', onConnectionDragMouseMove );

                      // unmark dragged step element
                      document.getElementById( `${uid}_businessProcessStep_${dragInfo.current?.dragLayoutElementId}` )?.classList.remove( 'yn-step-dragged' );
                      document.getElementById( `${uid}_businessProcessStep_${dragInfo.current?.dragLayoutElementId}` )?.classList.remove( 'yn-connection-dragged' );

                      // unmark valid drop steps
                      if ( elements?.length ) {
                          for ( let i = 0; i < elements.length; i++ ) {
                              elements[ i ].classList.remove( 'yn-valid-for-drop' );
                          }
                      }

                      // clean temp dataContext
                      delete global.data[ tempDataContext ];
                      clearElusiveData( tempDataContext );

                      // destroy drag ghost
                      if ( dragInfo.current?.dragElement ) {
                          document.body.removeChild( dragInfo.current.dragElement );
                      }

                      // clean drag info
                      dragInfo.current = null;
                  },
                  [
                      onConnectionDragMouseMove,
                      uid
                  ]
              ),
              onConnectionDragOver                                              = useCallback(
                  ( event : any ) => {
                      event.preventDefault();
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onConnectionDragEnter                                             = useCallback(
                  ( event : any, step : BusinessProcessStep ) => {
                      if ( dragInfo.current?.dragLayoutElementId ) {
                          dragInfo.current.referenceId = dispatch( addReference(
                              tempDataContext,
                              {
                                  referenceSetId: businessProcessStepIsConnectedToBusinessProcessStepReferenceSetId,
                                  srcItemId     : dragInfo.current.dragLayoutElementId,
                                  destItemId    : step.data.id
                              } as ReferenceAttributesForCreation
                          ) ).id;
                      }
                  },
                  [
                      tempDataContext
                  ]
              ),
              onConnectionDragLeave                                             = useCallback(
                  () => {
                      if ( dragInfo.current?.referenceId ) {
                          dispatch( deleteReference(
                              tempDataContext,
                              dragInfo.current.referenceId
                          ) );
                      }
                  },
                  [
                      tempDataContext
                  ]
              ),
              onConnectionDrop                                                  = useCallback(
                  ( event : any, step : BusinessProcessStep ) => {
                      const reference = global.data[ tempDataContext ].references[ dragInfo.current?.referenceId ?? 0 ];

                      event.preventDefault();

                      if ( reference.isNew() ) {
                          dispatch( addReference(
                              configDataContext,
                              {
                                  referenceSetId: reference.referenceSetId,
                                  srcItemId     : reference.srcItemId,
                                  destItemId    : reference.destItemId
                              } as ReferenceAttributesForCreation,
                              reference.schema,
                              additionalElusiveDataContexts
                          ) );
                      }

                      onConnectionDragEnd( event );
                  },
                  [
                      additionalElusiveDataContexts,
                      configDataContext,
                      onConnectionDragEnd
                  ]
              ),
              onConnectionDragStart                                             = useCallback(
                  ( event : any, step : BusinessProcessStep ) => {
                      const elements = document.querySelectorAll( '.yn-businessprocess-step-substep' );

                      // add listeners for drag control
                      document.querySelector( 'html' )?.addEventListener( 'mousemove', onConnectionDragMouseMove );

                      // init temp dataContext
                      for ( const item of Object.values( global.data[ configDataContext ]?.items ?? {} ) ) {
                          item.clone( undefined, undefined, tempDataContext ).commit();
                      }
                      for ( const reference of Object.values( global.data[ configDataContext ]?.references ?? {} ) ) {
                          reference.clone( undefined, undefined, tempDataContext ).commit();
                      }

                      // init drag info
                      dragInfo.current = {
                          type               : 'connection',
                          dragLayoutElementId: step.data.id
                      };

                      // mark dragged the step element
                      document.getElementById( `${uid}_businessProcessStep_${step.data.id}` )?.classList.remove( 'yn-step-dragged' );
                      document.getElementById( `${uid}_businessProcessStep_${step.data.id}` )?.classList.add( 'yn-connection-dragged' );

                      // mark valid drop steps
                      if ( elements?.length ) {
                          const invalidIds = [ `${uid}_businessProcessStep_${step.data.id}` ].concat( step.connections?.out.map( c => `${uid}_businessProcessStep_${c.step.id}` ) ?? [] );

                          for ( let i = 0; i < elements.length; i++ ) {
                              if ( !invalidIds.includes( elements[ i ].id ) ) {
                                  elements[ i ].classList.add( 'yn-valid-for-drop' );
                              }
                          }
                      }
                  },
                  [
                      onConnectionDragMouseMove
                  ]
              ),
              onConnectionClick                                                 = useCallback(
                  ( event : any, connection : Connection ) => {
                      onSelectStepOrConnection?.( connection.sourceStepId, connection.id );

                      event.preventDefault();
                      event.stopPropagation();
                  },
                  [
                      onSelectStepOrConnection,
                      uid
                  ]
              ),
              onConnectionOver                                                  = useCallback(
                  ( event : any, connection : Connection ) => {
                      const globalStyle       = window.getComputedStyle( document.documentElement ),
                            svgLinkHoverColor = globalStyle.getPropertyValue( '--businessprocess-connection-hover-color' );

                      let elements = document.querySelectorAll( '.yn-businessprocess-connection-path' );

                      if ( elements?.length ) {
                          for ( let i = 0; i < elements.length; i++ ) {
                              if ( elements[ i ].id !== `${uid}_${connection.id}_path` ) {
                                  elements[ i ].classList.add( 'yn-opaque' );
                              }
                          }
                      }

                      elements = document.querySelectorAll( '.yn-businessprocess-step-substep' );

                      if ( elements?.length ) {
                          for ( let i = 0; i < elements.length; i++ ) {
                              if ( elements[ i ].id !== `${uid}_businessProcessStep_${connection.sourceStepId}` && elements[ i ].id !== `${uid}_businessProcessStep_${connection.targetStepId}` ) {
                                  elements[ i ].classList.add( 'yn-opaque' );
                              }
                              else {
                                  elements[ i ].classList.add( 'yn-hover' );
                              }
                          }
                      }

                      graphSvg.current
                          ?.select( `#${uid}_${connection.id}_path` )
                          .attr( 'stroke', svgLinkHoverColor )
                          .attr( 'marker-end', `url(#${arrowMarkerId}_hover)` );
                  },
                  [
                      arrowMarkerId,
                      uid
                  ]
              ),
              onConnectionOut                                                   = useCallback(
                  ( event : any, connection : Connection ) => {
                      const globalStyle          = window.getComputedStyle( document.documentElement ),
                            svgLinkColor         = globalStyle.getPropertyValue( '--businessprocess-connection-color' ),
                            svgLinkSelectedColor = globalStyle.getPropertyValue( '--businessprocess-connection-selected-color' );

                      let elements = document.querySelectorAll( '.yn-businessprocess-connection-path' );

                      if ( elements?.length ) {
                          for ( let i = 0; i < elements.length; i++ ) {
                              elements[ i ].classList.remove( 'yn-opaque' );
                          }
                      }

                      elements = document.querySelectorAll( '.yn-businessprocess-step-substep' );

                      if ( elements?.length ) {
                          for ( let i = 0; i < elements.length; i++ ) {
                              elements[ i ].classList.remove( 'yn-opaque' );
                              elements[ i ].classList.remove( 'yn-hover' );

                          }
                      }

                      graphSvg.current
                          ?.select( `#${uid}_${connection.id}_path` )
                          .attr( 'stroke', connection.color ?? svgLinkColor )
                          .attr( 'stroke', d => {
                              let retVal = svgLinkColor;

                              if ( selectedConnectionId === connection.id ) {
                                  retVal = svgLinkSelectedColor;
                              }
                              else if ( connection.color ) {
                                  retVal = connection.color;
                              }

                              return retVal;
                          } )
                          .attr( 'marker-end', d => {
                              let markerId = arrowMarkerId;

                              if ( selectedConnectionId === connection.id ) {
                                  markerId = `${arrowMarkerId}_selected`;
                              }
                              else if ( connection.color ) {
                                  markerId = `${arrowMarkerId}_${connection.color.substring( 1 )}`;
                              }

                              return `url(#${markerId})`;
                          } );
                  },
                  [
                      arrowMarkerId,
                      selectedConnectionId,
                      uid
                  ]
              ),
              onAddStep                                                         = useCallback(
                  () => {
                      // reorder
                      if ( cBPS.current && cSEB.current ) {
                          for ( let stepIndex = 0; stepIndex < cBPS.current.length; stepIndex++ ) {
                              for ( let subStepIndex = 0; subStepIndex < cBPS.current[ stepIndex ].length; subStepIndex++ ) {
                                  const subStep = cBPS.current[ stepIndex ][ subStepIndex ];

                                  dispatch( updateItem(
                                      tempDataContext,
                                      subStep.data.id,
                                      {
                                          properties: {
                                              [ orderPropertyTypeIdString ]   : {
                                                  value: stepIndex + 1
                                              },
                                              [ subOrderPropertyTypeIdString ]: {
                                                  value: subStepIndex + 1
                                              }
                                          }
                                      }
                                  ) );
                              }
                          }
                      }

                      // append new
                      const newStep = dispatch( addItem(
                          configDataContext,
                          {
                              itemTypeId: businessProcessStepItemTypeId,
                              key       : '',
                              name      : 'new BusinessProcessStep',
                              properties: [
                                  {
                                      propertySetId: businessProcessStepActivePropertySetId,
                                      value        : true
                                  },
                                  {
                                      propertySetId: businessProcessStepOrderPropertySetId,
                                      value        : ( cBPS.current?.length ?? 0 ) + 1
                                  },
                                  {
                                      propertySetId: businessProcessStepSubOrderPropertySetId,
                                      value        : 1
                                  }
                              ]
                          } as ItemAttributesForCreation,
                          'CFG',
                          additionalElusiveDataContexts
                      ) );

                      if ( newStep ) {
                          dispatch( addReference(
                              configDataContext,
                              {
                                  referenceSetId: businessProcessStepBelongsToBusinessProcessReferenceSetId,
                                  srcItemId     : newStep.id,
                                  destItemId    : businessProcessItem.id
                              } as ReferenceAttributesForCreation,
                              'CFG',
                              additionalElusiveDataContexts
                          ) );

                          onSelectStepOrConnection?.( newStep.id, undefined );
                      }
                  },
                  [
                      additionalElusiveDataContexts,
                      configDataContext,
                      onSelectStepOrConnection,
                      uid
                  ]
              ),
              onDeleteConnectionClick                                           = useCallback(
                  ( event : any ) => {
                      if ( selectedConnectionId ) {
                          dispatch( deleteReference(
                              configDataContext,
                              selectedConnectionId,
                              true,
                              additionalElusiveDataContexts
                          ) );

                          onSelectStepOrConnection?.( configRootItemId, undefined );
                      }
                  },
                  [
                      additionalElusiveDataContexts,
                      configDataContext,
                      onSelectStepOrConnection,
                      selectedConnectionId,
                      uid
                  ]
              ),
              onBackgroundClick                                                 = useCallback(
                  ( event : MouseEvent ) => {
                      const target    = event.target as HTMLElement,
                            contentEl = document.getElementById( `${onBackgroundClickProps.current?.uid}_businessProcess` );

                      if (
                          contentEl
                          && (
                              target.tagName.toUpperCase() === 'SVG'
                              || target.classList.contains( 'yn-businessprocess-step' )
                              || !contentEl.contains( target )
                          )
                      ) {
                          onBackgroundClickProps.current?.onSelectStepOrConnection?.( onBackgroundClickProps.current?.configRootItemId, undefined );
                      }

                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onParanthesesMouseOver                                            = useCallback(
                  ( parenthesesCount : number ) => {
                      const elements = document.querySelectorAll( `.yn-businessprocess-condition-operator-${parenthesesCount}, .yn-businessprocess-condition-parentheses-${parenthesesCount}` );

                      let index = 0;

                      if ( elements?.length ) {
                          while ( elements[ index ] ) {
                              elements[ index++ ].classList.add( 'yn-businessprocess-condition-element-over' );
                          }
                      }
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onParanthesesMouseOut                                             = useCallback(
                  ( parenthesesCount : number ) => {
                      const elements = document.querySelectorAll( `.yn-businessprocess-condition-operator-${parenthesesCount}, .yn-businessprocess-condition-parentheses-${parenthesesCount}` );

                      let index = 0;

                      if ( elements?.length ) {
                          while ( elements[ index ] ) {
                              elements[ index++ ].classList.remove( 'yn-businessprocess-condition-element-over' );
                          }
                      }
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onConditionClick                                                  = useCallback(
                  ( step : BusinessProcessStep ) => {
                      setShowConditionEditorDialog( true );
                      setStepOfConditionToEdit( step );
                      setConditionToEdit( step.condition ?? null );
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onConditionEditorDialogOkButtonClick                              = useCallback(
                  () => {
                      setShowConditionEditorDialog( false );
                      setStepOfConditionToEdit( null );
                      setConditionToEdit( null );
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onConditionEditorDialogHide                                       = useCallback(
                  () => {
                      setShowConditionEditorDialog( false );
                      setStepOfConditionToEdit( null );
                      setConditionToEdit( null );
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onDeleteConditionClick                                            = useCallback(
                  ( condition : Condition ) => {
                      // TODO: implement delete condition
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onConditionTypeChange                                             = useCallback(
                  ( condition : Condition | null, step : BusinessProcessStep ) => {
                      // TODO: implement condition type change
                      // step is for new
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              resolveConditionForFieldExpressionLabel                           = ( expression : string | Condition[] | undefined, dataDescriptor? : DataDescriptor | undefined, dataStructure? : DataStructure | undefined, attribute? : string | undefined, propertySet? : PropertySet | undefined ) : string => {
                  const stringTypes = [ 'COLOR', 'EMAIL', 'IBAN', 'JSON', 'LINK', 'PHONE', 'STRING', 'TEXT', 'TIMERULE' ];

                  let innerRetVal = '',
                      dataObject : DataStructure | undefined,
                      dataObjectSchema : Schema | undefined;


                  // TODO:
                  //  - specials
                  //      - at least one referenced items (for a specified ReferenceSet) meet a condition
                  //      - all referenced items (for a specified ReferenceSet) meet a condition (e.g. all child tasks status equals to solved)


                  if ( dataDescriptor?.dataObject !== undefined ) {
                      dataObject = dataDescriptor.dataObject;
                      dataObjectSchema = ( dataObject.item.objectSchema ?? dataStructure?.item.objectSchema ?? 'data' ).toUpperCase() as Schema;

                      if ( dataObject?.item?.objectType !== undefined ) {
                          if ( !propertySet && dataDescriptor.type === 'property' ) {
                              propertySet = metaModel[ dataObjectSchema ]?.getPropertySetForItemTypeAndPropertyType( dataObject.item.objectType, dataDescriptor.propertyTypeName );
                          }

                          // TODO: ReferenceSet
                          // TODO: ReferencePropertySet
                      }
                  }

                  if ( expression !== undefined && typeof expression === 'string' ) {
                      innerRetVal = expression;

                      if ( attribute === 'value' ) {
                          if ( propertySet?.valueType === 'BOOLEAN' ) {
                              if ( convertToBoolean( innerRetVal ) ) {
                                  innerRetVal = __( 'YN.base.common.true' );
                              }
                              else {
                                  innerRetVal = __( 'YN.base.common.false' );
                              }
                          }
                          else if ( propertySet?.valueSetId ) {
                              innerRetVal = metaModel[ propertySet.schema ]?.getValueSetValue( propertySet.valueSetId, String( innerRetVal ) )?.label ?? innerRetVal;
                          }
                      }

                      // string attribute? -> add quotes
                      if (
                          (
                              attribute === 'key'
                              && propertySet?.keyType !== undefined
                              && stringTypes.includes( propertySet.keyType )
                          )
                          || (
                              attribute === 'value'
                              && propertySet?.valueType !== undefined
                              && stringTypes.includes( propertySet.valueType )
                          )
                      ) {
                          innerRetVal = `'${innerRetVal}'`;
                      }
                  }
                  else if ( dataDescriptor && dataObject ) {
                      innerRetVal += dataObject?.item.name ?? '';

                      if ( propertySet ) {
                          innerRetVal += `-${propertySet.label}`;

                          if ( dataDescriptor.attribute === 'key' ) {
                              innerRetVal += ` ${propertySet.keyLabel}`;
                          }
                          else if ( dataDescriptor.attribute !== 'value' ) {
                              innerRetVal += ` ${__( `YN.base.common.attribute.${dataDescriptor.attribute}` )}`;
                          }
                      }

                      // TODO: referenceSet
                      // TODO: referencePropertySet
                  }

                  return innerRetVal;
              },
              resolveConditionForFieldRecursive                                 = ( condition : Condition, dataStructure : DataStructure, parenthesesCount : number = 0 ) : React.ReactNode => {
                  let retVal : React.ReactNode = null,
                      attribute : string | undefined,
                      propertySet : PropertySet | undefined;

                  // TODO: ReferenceSet
                  // TODO: ReferencePropertySet

                  if ( condition !== undefined && ( condition?.leftExpression !== undefined || condition.leftExpressionDataDescriptor !== undefined ) ) {

                      if ( ( condition.operator === 'and' || condition.operator === 'or' ) && Array.isArray( condition.leftExpression ) ) {
                          parenthesesCount++;

                          const resolvedSubConditions = condition.leftExpression.map( subCondition => resolveConditionForFieldRecursive( subCondition, dataStructure, parenthesesCount ) ),
                                color                 = getIntenseColorFromColorPalette( parenthesesCount )

                          let localizedOperatorLabel : React.ReactNode = __( `YN.base.operator.${condition.operator}` ),
                              operatorCount                            = 0;

                          if ( !isInvalidHTML( localizedOperatorLabel as string ) ) {
                              localizedOperatorLabel = parse( localizedOperatorLabel as string );
                          }

                          /* eslint-disable jsx-a11y/mouse-events-have-key-events, jsx-a11y/no-static-element-interactions */
                          for ( let i = resolvedSubConditions.length - 1; i > 0; i-- ) {
                              resolvedSubConditions.splice(
                                  i,
                                  0,
                                  <>
                                      &nbsp;
                                      <span
                                          key={`operator_${parenthesesCount}`}
                                          id={`${uid}_operator_${parenthesesCount}_${operatorCount++}`}
                                          className={`yn-businessprocess-condition-element yn-businessprocess-condition-operator yn-businessprocess-condition-operator-${parenthesesCount}`}
                                          style={{ [ '--color' ]: color }}
                                          onMouseOver={onParanthesesMouseOver.bind( null, parenthesesCount )}
                                          onMouseOut={onParanthesesMouseOut.bind( null, parenthesesCount )}
                                      >
                                          {localizedOperatorLabel}
                                      </span>
                                      &nbsp;
                                  </>
                              );
                          }

                          retVal = [
                              <span
                                  key={`parentheses_open_${parenthesesCount}`}
                                  id={`${uid}_parentheses_open_${parenthesesCount}`}
                                  className={`yn-businessprocess-condition-element yn-businessprocess-condition-parentheses yn-businessprocess-condition-parentheses-open yn-businessprocess-condition-parentheses-${parenthesesCount}`}
                                  style={{ [ '--color' ]: color }}
                                  onMouseOver={onParanthesesMouseOver.bind( null, parenthesesCount )}
                                  onMouseOut={onParanthesesMouseOut.bind( null, parenthesesCount )}
                              >
                                  (&nbsp;
                              </span>,
                              ...resolvedSubConditions,
                              <span
                                  key={`parentheses_close_${parenthesesCount}`}
                                  id={`${uid}_parentheses_close_${parenthesesCount}`}
                                  className={`yn-businessprocess-condition-element yn-businessprocess-condition-parentheses yn-businessprocess-condition-parentheses-close yn-businessprocess-condition-parentheses-${parenthesesCount}`}
                                  style={{ [ '--color' ]: color }}
                                  onMouseOver={onParanthesesMouseOver.bind( null, parenthesesCount )}
                                  onMouseOut={onParanthesesMouseOut.bind( null, parenthesesCount )}
                              >
                                  &nbsp;)
                              </span>
                          ];
                          /* eslint-enable jsx-a11y/mouse-events-have-key-events, jsx-a11y/no-static-element-interactions */
                      }
                      else {
                          attribute = condition.leftExpressionDataDescriptor?.attribute;

                          if ( condition.leftExpressionDataDescriptor?.dataObject !== undefined ) {
                              const dataObject       = condition.leftExpressionDataDescriptor.dataObject,
                                    dataObjectSchema = ( dataObject.item.objectSchema ?? dataStructure?.item.objectSchema ?? 'data' ).toUpperCase() as Schema;

                              if ( dataObject?.item?.objectType !== undefined ) {
                                  if ( condition.leftExpressionDataDescriptor.type === 'property' ) {
                                      propertySet = metaModel[ dataObjectSchema ]?.getPropertySetForItemTypeAndPropertyType( dataObject.item.objectType, condition.leftExpressionDataDescriptor.propertyTypeName );
                                  }

                                  // TODO: ReferenceSet
                                  // TODO: ReferencePropertySet
                              }
                          }

                          // left expression
                          retVal = `<b>${resolveConditionForFieldExpressionLabel(
                              condition.leftExpression,
                              condition.leftExpressionDataDescriptor,
                              dataStructure
                          )}</b>`;

                          // operator
                          retVal += ` ${__( `YN.base.operator.${condition.operator}` )}`;

                          // right expression
                          if ( condition.operator === 'in' && Array.isArray( condition.rightExpression ) ) {
                              const values = condition.rightExpression.map( ( subCondition : Condition ) => {
                                  return resolveConditionForFieldExpressionLabel(
                                      subCondition.leftExpression,
                                      subCondition.leftExpressionDataDescriptor,
                                      dataStructure,
                                      attribute,
                                      propertySet
                                  );
                              } );

                              retVal += ` [ ${values.join( ', ' )} ]`;
                          }
                          else if ( condition.operator !== 'empty' && condition.operator !== 'notEmpty' ) {
                              retVal += ` ${resolveConditionForFieldExpressionLabel(
                                  condition.rightExpression,
                                  condition.rightExpressionDataDescriptor,
                                  dataStructure,
                                  attribute,
                                  propertySet
                              )}`;
                          }

                          if ( !isInvalidHTML( retVal as string ) ) {
                              retVal = parse( ( retVal as string ).replaceAll( ' ', '&nbsp;' ) );
                          }

                          retVal = <span className="yn-chiplike yn-chiplike-condition">
                                        {retVal}
                                    </span>;
                      }
                  }

                  return retVal;
              },
              getConditionField                                                 = ( step : BusinessProcessStep, dataStructure? : DataStructure ) : React.ReactNode => {
                  let retVal : React.ReactNode = null;

                  if ( dataStructure ) {
                      if ( step.condition ) {
                          retVal = resolveConditionForFieldRecursive( step.condition, dataStructure );
                      }
                      else {
                          retVal = <span className="yn-chiplike yn-chiplike-condition yn-chiplike-condition-empty">
                          {__( 'YN.base.frontend.businessProcessDesigner.condition.empty' )}
                      </span>;
                      }

                      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                      retVal = <div
                          className="yn-fieldlike yn-fieldlike-condition"
                          onClick={onConditionClick.bind( null, step )}
                      >
                          {retVal}
                      </div>;
                  }

                  return retVal;
              },
              getConditionTypeField                                             = ( condition : Condition | null, step : BusinessProcessStep ) : React.ReactNode => {
                  const options = [
                      {
                          value: 'compare',
                          label: __( 'YN.base.common.condition.type.compare.label' )
                      },
                      {
                          value: 'and',
                          label: __( 'YN.base.common.condition.type.and.label' )
                      },
                      {
                          value: 'or',
                          label: __( 'YN.base.common.condition.type.or.label' )
                      }
                  ];

                  let type = 'compare';

                  if ( condition?.operator === 'and' || condition?.operator === 'or' ) {
                      type = condition.operator;
                  }

                  return <Dropdown
                      key="condition_type_dropdown"
                      value={type}
                      onChange={onConditionTypeChange.bind( null, condition, step )}
                      options={options}
                      dataKey="value"
                      optionLabel="label"
                      className="yn-businessprocess-condition-editor-field yn-businessprocess-condition-editor-field-type"
                      style={{ width: type === 'compare' ? '8rem' : '36rem' }}
                  />;
              },
              getConditionOperatorField                                         = ( condition : Condition | null ) => {
                  let retVal : React.ReactNode = null;


                  // TODO: dropdown with all operators than AND and OR depending on ValueType


                  retVal = <div
                      className="yn-businessprocess-condition-editor-field yn-businessprocess-condition-editor-field-operator"
                  >
                      text
                  </div>;

                  return retVal;
              },
              resolveConditionEditorField                                       = ( condition : Condition | null, step : BusinessProcessStep, dataStructure : DataStructure, parentCondition? : Condition ) : React.ReactNode => {
                  const retVal : React.ReactNode[] = [
                      // delete button
                      <Button
                          key="condition_delete_button"
                          icon="pi pi-times"
                          className={`p-button-danger yn-businessprocess-condition-editor-field yn-businessprocess-condition-editor-field-delete${condition === null ? ' yn-businessprocess-condition-editor-field-delete-placeholder' : ''}`}
                          rounded={true}
                          text={true}
                          onClick={condition ? onDeleteConditionClick.bind( null, condition ) : undefined}
                      />,
                      // type dropdown
                      getConditionTypeField( condition, step )
                  ];

                  if ( condition?.operator !== 'and' && condition?.operator !== 'or' ) {

                      // leftExpression field
                      // static value
                      if ( parentCondition?.operator === 'in' || parentCondition?.operator === 'notIn' ) {


                          // TODO: implement


                          retVal.push( <div
                              className="yn-businessprocess-condition-editor-field yn-businessprocess-condition-editor-field-expression-left"
                          >
                              implement left expression
                          </div> );
                      }
                      // dataDescriptor or special
                      else {

                          // TODO:
                          //    - special
                          //        - at least one referenced items (for a specified ReferenceSet) meet a condition
                          //        - all referenced items (for a specified ReferenceSet) meet a condition (e.g. all child tasks status equals to solved)


                          // TODO: here we go


                          retVal.push( <div
                              className="yn-businessprocess-condition-editor-field yn-businessprocess-condition-editor-field-expression-left"
                          >
                              implement left expression
                          </div> );
                      }

                      // operator field
                      if ( parentCondition?.operator !== 'in' && parentCondition?.operator !== 'notIn' ) {
                          retVal.push( getConditionOperatorField( condition ) );
                      }
                      // placeholder
                      else {
                          retVal.push( <div
                              className="yn-businessprocess-condition-editor-field yn-businessprocess-condition-editor-field-operator yn-businessprocess-condition-editor-field-operator-placeholder"
                          /> );
                      }

                      // rightExpression field
                      // static value
                      if (
                          parentCondition?.operator !== 'in' && parentCondition?.operator !== 'notIn'
                          && condition?.operator !== 'empty' && condition?.operator !== 'notEmpty'
                      ) {


                          // TODO: implement


                          retVal.push( <div
                                  className="yn-businessprocess-condition-editor-field yn-businessprocess-condition-editor-field-expression-right"
                              >
                                  implement right expression
                              </div>
                          );
                      }
                      // placeholder
                      else {
                          retVal.push( <div
                              className="yn-businessprocess-condition-editor-field yn-businessprocess-condition-editor-field-expression-right yn-businessprocess-condition-editor-field-expression-right-placeholder"
                          /> );
                      }
                  }

                  return <div
                      className="yn-businessprocess-condition-editor-fields"
                  >
                      {retVal}
                  </div>;
              },
              resolveConditionEditorFieldsRecursive                             = ( condition : Condition | null, step : BusinessProcessStep, dataStructure : DataStructure ) : React.ReactNode => {
                  let retVal : React.ReactNode = null;

                  if ( condition && ( condition.operator === 'and' || condition.operator === 'or' ) ) {
                      const resolvedSubConditions = Array.isArray( condition.leftExpression )
                          ? condition.leftExpression.map( subCondition => resolveConditionEditorFieldsRecursive( subCondition, step, dataStructure, condition ) )
                          : null;

                      retVal = <>
                          {resolveConditionEditorField( condition, step, dataStructure )}
                          <div
                              className="yn-businessprocess-condition-editor-parenthesis"
                          >
                              {resolvedSubConditions}
                              {resolveConditionEditorField( null, step, dataStructure )}
                          </div>
                      </>;
                  }
                  else {
                      retVal = resolveConditionEditorField( condition, step, dataStructure );
                  }

                  return retVal;
              },
              getConditionEditorFields                                          = useCallback(
                  () => {
                      let retVal : React.ReactNode = null;

                      if ( stepOfConditionToEdit && businessProcess?.dataStructure ) {
                          retVal = resolveConditionEditorFieldsRecursive( conditionToEdit, stepOfConditionToEdit, businessProcess.dataStructure );
                      }

                      return retVal;
                  },
                  [
                      businessProcess?.dataStructure,
                      conditionToEdit,
                      stepOfConditionToEdit
                  ]
              ),
              getStepContent                                                    = useCallback(
                  ( step : BusinessProcessStep ) => {
                      const title = step.messages?.title && typeof step.messages.title === 'string'
                          ? __( step.messages.title )
                          : step.data.name;

                      /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
                      return <div
                          key={`businessProcessStep_${step.data.id}`}
                          id={`${uid}_businessProcessStep_${step.data.id}`}
                          className={`yn-businessprocess-step-substep${selectedStepId === step.data.id && !selectedConnectionId ? ' yn-selected' : ''}${dragInfo.current?.dragLayoutElementId === step.data.id ? dragInfo.current.type === 'step' ? ' yn-step-dragged' : ' yn-connection-dragged' : ''}`}
                          onClick={( event : any ) => onStepClick( event, step )}
                          onMouseOver={( event : any ) => onStepOver( event, step )}
                          onFocus={( event : any ) => onStepOver( event, step )}
                          onMouseOut={( event : any ) => onStepOut( event, step )}
                          onBlur={( event : any ) => onStepOut( event, step )}
                      >
                          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                          <div
                              className="yn-businessprocess-step-delete pi pi-minus-circle"
                              onClick={( event : any ) => onStepDeleteClick( event, step )}
                          ></div>
                          <div
                              className="yn-businessprocess-step-connections-in material-symbols-outlined"
                              onDragOver={onConnectionDragOver}
                              onDragLeave={onConnectionDragLeave}
                              onDragEnter={( event : any ) => onConnectionDragEnter( event, step )}
                              onDrop={( event : any ) => onConnectionDrop( event, step )}
                          >
                              conversion_path
                          </div>
                          <div
                              className="yn-businessprocess-step-connections-out material-symbols-outlined"
                              draggable={true}
                              onDragStart={( event : any ) => onConnectionDragStart( event, step )}
                              onDragEnd={( event : any ) => onConnectionDragEnd( event )}
                          >
                              conversion_path
                          </div>
                          <Card
                              title={title}
                              pt={{
                                  root: {
                                      draggable  : true,
                                      onDragStart: ( event : any ) => onStepDragStart( event, step )
                                  }
                              }}
                          >
                              {getConditionField( step, businessProcess?.dataStructure )}
                          </Card>
                      </div>
                  },
                  [
                      businessProcess?.dataStructure,
                      language,
                      onConnectionDragEnd,
                      onConnectionDragEnter,
                      onConnectionDragLeave,
                      onConnectionDragOver,
                      onConnectionDragStart,
                      onConnectionDrop,
                      onStepClick,
                      onStepDeleteClick,
                      onStepDragStart,
                      onStepOut,
                      onStepOver,
                      selectedStepId,
                      selectedConnectionId,
                      uid
                  ]
              ),
              connections : Connection[ ]                                       = useMemo(
                  () => {
                      const retVal : Connection[]              = [],
                            topLanes : [ number, number ][]    = [],
                            bottomLanes : [ number, number ][] = [],
                            nodeLinkSpacing                    = 10,
                            laneSpacing                        = 6,
                            laneOffset                         = 12,
                            cornerRadius                       = 6,
                            arrowWidth                         = 14,
                            arrowOffsetStart                   = 12,
                            arrowOffsetEnd                     = 6,
                            stepSpacing                        = 96;

                      if ( businessProcess?.steps ) {
                          for ( let stepIndex = 0; stepIndex < businessProcess.steps.length; stepIndex++ ) {
                              const step                           = businessProcess.steps[ stepIndex ] as BusinessProcessStep | BusinessProcessStep[],
                                    steps                          = Array.isArray( step ) ? step : [ step ],
                                    siblingSteps                   = ( [] as BusinessProcessStep[] ).concat( step ),
                                    siblingStepKeys                = siblingSteps.map( s => s.data.key ),
                                    nextSteps                      = businessProcess.steps[ stepIndex + 1 ]
                                        ? ( [] as BusinessProcessStep[] ).concat( businessProcess.steps[ stepIndex + 1 ] )
                                        : [],
                                    siblingTotalOutConnectionCount = siblingSteps.reduce(
                                        ( acc, s ) => acc + ( s.connections?.out?.length ?? 0 ),
                                        0
                                    );

                              for ( let subStepIndex = 0; subStepIndex < steps.length; subStepIndex++ ) {
                                  const subStep = steps[ subStepIndex ];

                                  if ( subStep.connections?.out?.length ) {
                                      const startCardElBox = stepElementBoxes[ subStep.data.id ];

                                      if ( offsetElBox && startCardElBox ) {
                                          const // x pos of connection start
                                              startX                                  = startCardElBox.left + startCardElBox.width - offsetElBox.left,
                                              // base y pos of connection start
                                              startOffsetY                            = startCardElBox.top + startCardElBox.height / 2 - offsetElBox.top,
                                              // base y position of forward links which skip next step(s)
                                              forwardLaneOffsetY                      = Math.max( ...Object.values( stepElementBoxes ?? {} ).map( el => el.bottom ) ?? [ 0 ] ) - offsetElBox.top + laneOffset,
                                              // base y position of the backward links
                                              backLaneOffsetY                         = Math.min( ...Object.values( stepElementBoxes ?? {} ).map( el => el.top ) ?? [ 0 ] ) - offsetElBox.top - laneOffset,
                                              connectionCount                         = subStep.connections.out.length,
                                              backConnectionsCount                    = subStep.connections.out.reduce(
                                                  ( acc, connection ) => {
                                                      const targetStep = businessProcessSteps.find( bps => bps.data.key === connection.step.key );

                                                      if ( targetStep && targetStep.data.order <= subStep.data.order ) {
                                                          acc++;
                                                      }

                                                      return acc;
                                                  },
                                                  0
                                              ),
                                              forwardConnectionsCount                 = subStep.connections.out.reduce(
                                                  ( acc, connection ) => {
                                                      const targetStepIndex = businessProcess?.steps?.findIndex( s2 => ( [] as BusinessProcessStep[] ).concat( s2 ).find( bps => bps.data.key === connection.step.key ) ) ?? 0;

                                                      if ( targetStepIndex > stepIndex + 1 ) {
                                                          acc++;
                                                      }

                                                      return acc;
                                                  },
                                                  0
                                              ),
                                              linkConnectionsCount                    = connectionCount - backConnectionsCount - forwardConnectionsCount,
                                              previousSiblingSteps                    = siblingSteps.reduce(
                                                  ( acc, s ) => {
                                                      if ( s.data.subOrder < subStep.data.subOrder ) {
                                                          acc.push( s );
                                                      }

                                                      return acc;
                                                  },
                                                  [] as BusinessProcessStep[]
                                              ),
                                              nextSiblingSteps                        = siblingSteps.reduce(
                                                  ( acc, s ) => {
                                                      if ( s.data.subOrder > subStep.data.subOrder ) {
                                                          acc.push( s );
                                                      }

                                                      return acc;
                                                  },
                                                  [] as BusinessProcessStep[]
                                              ),
                                              previousSiblingsTotalOutConnectionCount = previousSiblingSteps.reduce(
                                                  ( acc, s ) => acc + ( s.connections?.out?.length ?? 0 ),
                                                  0
                                              ),
                                              previousSiblingsForwardConnectionsCount = previousSiblingSteps.reduce(
                                                  ( acc, s ) => {
                                                      if ( s.connections?.out?.length ) {
                                                          acc += s.connections.out.reduce(
                                                              ( acc2, connection ) => {
                                                                  const targetStepIndex = businessProcess?.steps?.findIndex( s2 => ( [] as BusinessProcessStep[] ).concat( s2 ).find( bps => bps.data.key === connection.step.key ) ) ?? 0;

                                                                  if ( targetStepIndex > stepIndex + 1 ) {
                                                                      acc2++;
                                                                  }

                                                                  return acc2;
                                                              },
                                                              0
                                                          );
                                                      }

                                                      return acc;
                                                  },
                                                  0
                                              ),
                                              previousSiblingsBackConnectionsCount    = previousSiblingSteps.reduce(
                                                  ( acc, s ) => {
                                                      if ( s.connections?.out?.length ) {
                                                          acc += s.connections.out.reduce(
                                                              ( acc2, connection ) => {
                                                                  const targetStep = businessProcessSteps.find( bps => bps.data.key === connection.step.key );

                                                                  if ( targetStep && targetStep.data.order <= s.data.order ) {
                                                                      acc2++;
                                                                  }

                                                                  return acc2;
                                                              },
                                                              0
                                                          );
                                                      }

                                                      return acc;
                                                  },
                                                  0
                                              ),
                                              previousSiblingsLinkConnectionsCount    = previousSiblingsTotalOutConnectionCount - previousSiblingsBackConnectionsCount - previousSiblingsForwardConnectionsCount,
                                              nextSiblingsForwardConnectionsCount     = nextSiblingSteps.reduce(
                                                  ( acc, s ) => {
                                                      if ( s.connections?.out?.length ) {
                                                          acc += s.connections.out.reduce(
                                                              ( acc2, connection ) => {
                                                                  const targetStepIndex = businessProcess?.steps?.findIndex( s2 => ( [] as BusinessProcessStep[] ).concat( s2 ).find( bps => bps.data.key === connection.step.key ) ) ?? 0;

                                                                  if ( targetStepIndex > stepIndex + 1 ) {
                                                                      acc2++;
                                                                  }

                                                                  return acc2;
                                                              },
                                                              0
                                                          );
                                                      }

                                                      return acc;
                                                  },
                                                  0
                                              ),
                                              nextSiblingsBackConnectionsCount        = nextSiblingSteps.reduce(
                                                  ( acc, s ) => {
                                                      if ( s.connections?.out?.length ) {
                                                          acc += s.connections.out.reduce(
                                                              ( acc2, connection ) => {
                                                                  const targetStep = businessProcessSteps.find( bps => bps.data.key === connection.step.key );

                                                                  if ( targetStep && targetStep.data.order <= s.data.order ) {
                                                                      acc2++;
                                                                  }

                                                                  return acc2;
                                                              },
                                                              0
                                                          );
                                                      }

                                                      return acc;
                                                  },
                                                  0
                                              ),
                                              totalConnectionCount                    = siblingTotalOutConnectionCount + nextSteps.reduce(
                                                  ( acc, s ) => acc + ( s.connections?.in?.filter( ( connection ) => !siblingStepKeys.includes( connection.step.key ) ).length ?? 0 ),
                                                  0
                                              ),
                                              totalBackConnectionsCount               = backConnectionsCount + previousSiblingsBackConnectionsCount + nextSiblingsBackConnectionsCount,
                                              totalForwardConnectionsCount            = forwardConnectionsCount + previousSiblingsForwardConnectionsCount + nextSiblingsForwardConnectionsCount;

                                          let backLinkCount    = previousSiblingsBackConnectionsCount,
                                              forwardLinkCount = previousSiblingsForwardConnectionsCount,
                                              linkCount        = 0;

                                          for ( let targetIndex = 0; targetIndex < subStep.connections.out.length; targetIndex++ ) {
                                              const connection          = subStep.connections.out[ targetIndex ],
                                                    targetStep          = businessProcessSteps.find( s => s.data.key === connection.step.key ) as BusinessProcessStep,
                                                    targetStepIndex     = businessProcess?.steps?.findIndex( s => ( [] as BusinessProcessStep[] ).concat( s ).find( bps => bps === targetStep ) ) ?? 0,
                                                    isTargetCountOdd    = !!( subStep.connections.out.length % 2 ),
                                                    targetBaseNum       = Math.ceil( subStep.connections.out.length / -2 ) + targetIndex,
                                                    targetNum           = !isTargetCountOdd
                                                        ? targetBaseNum + .5
                                                        : targetBaseNum,
                                                    startY              = startOffsetY + nodeLinkSpacing * targetNum,
                                                    isBackLink          = targetStepIndex <= stepIndex,
                                                    isForwardConnection = targetStepIndex > stepIndex + 1;

                                              let startVLaneNum = 1;

                                              if ( isBackLink ) {
                                                  backLinkCount++;
                                                  startVLaneNum = backLinkCount;
                                              }
                                              else if ( isForwardConnection ) {
                                                  forwardLinkCount++;
                                                  startVLaneNum = totalBackConnectionsCount + totalForwardConnectionsCount - forwardLinkCount + 1;
                                              }
                                              else {
                                                  linkCount++;
                                                  startVLaneNum = totalBackConnectionsCount + totalForwardConnectionsCount + previousSiblingsLinkConnectionsCount + linkConnectionsCount - linkCount + 1;
                                              }

                                              if ( targetStep?.connections?.in.length ) {
                                                  const startVLaneX     = startX + arrowOffsetStart + startVLaneNum * ( ( stepSpacing - arrowOffsetStart - arrowOffsetEnd - arrowWidth / 2 ) / ( totalConnectionCount + 1 ) ),
                                                        targetCardElBox = stepElementBoxes[ targetStep.data.id ];

                                                  if ( targetCardElBox ) {
                                                      const targetX          = targetCardElBox.left - offsetElBox.left - arrowWidth / 2,
                                                            targetOffsetY    = targetCardElBox.top + targetCardElBox.height / 2 - offsetElBox.top,
                                                            sourceIndex      = targetStep.connections.in.findIndex( inConnection => inConnection.step.key === subStep.data.key ),
                                                            isSourceCountOdd = !!( targetStep.connections.in.length % 2 ),
                                                            sourceBaseNum    = Math.ceil( targetStep.connections.in.length / -2 ) + sourceIndex,
                                                            sourceNum        = !isSourceCountOdd
                                                                ? sourceBaseNum + .5
                                                                : sourceBaseNum,
                                                            targetY          = targetOffsetY + nodeLinkSpacing * sourceNum;

                                                      let cornerRadiusToUse = Math.min( cornerRadius, Math.abs( targetY - startY ) / 2 ),
                                                          line              = `M ${startX} ${startY}`;

                                                      if ( isBackLink || isForwardConnection ) {
                                                          const
                                                              targetPreviousSteps                        = businessProcess?.steps?.[ targetStepIndex - 1 ]
                                                                  ? ( [] as BusinessProcessStep[] ).concat( businessProcess.steps[ targetStepIndex - 1 ] )
                                                                  : [],
                                                              targetPreviousStepKeys                     = targetPreviousSteps.map( s => s.data.key ),
                                                              targetSiblingSteps                         = businessProcess?.steps?.[ targetStepIndex ]
                                                                  ? ( [] as BusinessProcessStep[] ).concat( businessProcess.steps[ targetStepIndex ] )
                                                                  : [],
                                                              targetPreviousSiblingSteps                 = targetSiblingSteps.reduce(
                                                                  ( acc, s ) => {
                                                                      if ( s.data.subOrder < targetStep.data.subOrder ) {
                                                                          acc.push( s );
                                                                      }

                                                                      return acc;
                                                                  },
                                                                  [] as BusinessProcessStep[]
                                                              ),
                                                              targetNextSiblingSteps                     = targetSiblingSteps.reduce(
                                                                  ( acc, s ) => {
                                                                      if ( s.data.subOrder > targetStep.data.subOrder ) {
                                                                          acc.push( s );
                                                                      }

                                                                      return acc;
                                                                  },
                                                                  [] as BusinessProcessStep[]
                                                              ),
                                                              targetSiblingsBackConnections              = targetSiblingSteps.reduce(
                                                                  ( acc, s ) => {
                                                                      if ( s.connections?.in?.length ) {
                                                                          acc += s.connections.in.reduce(
                                                                              ( acc2, inConnection ) => {
                                                                                  const sourceStep = businessProcessSteps.find( bps => bps.data.key === inConnection.step.key );

                                                                                  if ( sourceStep && sourceStep.data.order >= s.data.order ) {
                                                                                      acc2++;
                                                                                  }

                                                                                  return acc2;
                                                                              },
                                                                              0
                                                                          );
                                                                      }

                                                                      return acc;
                                                                  },
                                                                  0
                                                              ),
                                                              targetPreviousTotalOutConnectionCount      = targetPreviousSteps.reduce(
                                                                  ( acc, s ) => acc + ( s.connections?.out?.length ?? 0 ),
                                                                  0
                                                              ),
                                                              targetPreviousSiblingsBackConnectionsCount = targetPreviousSiblingSteps.reduce(
                                                                  ( acc, s ) => {
                                                                      if ( s.connections?.in?.length ) {
                                                                          acc += s.connections.in.reduce(
                                                                              ( acc2, inConnection ) => {
                                                                                  const sourceStep = businessProcessSteps.find( bps => bps.data.key === inConnection.step.key );

                                                                                  if ( sourceStep && sourceStep.data.order >= s.data.order ) {
                                                                                      acc2++;
                                                                                  }

                                                                                  return acc2;
                                                                              },
                                                                              0
                                                                          );
                                                                      }

                                                                      return acc;
                                                                  },
                                                                  0
                                                              ),
                                                              targetNextSiblingsForwardConnectionsCount  = targetNextSiblingSteps.reduce(
                                                                  ( acc, s ) => {
                                                                      if ( s.connections?.in?.length ) {
                                                                          acc += s.connections.in.reduce(
                                                                              ( acc2, inConnection ) => {
                                                                                  const sourceStepIndex = businessProcess?.steps?.findIndex( s2 => ( [] as BusinessProcessStep[] ).concat( s2 ).find( bps => bps.data.key === inConnection.step.key ) ) ?? 0;

                                                                                  if ( sourceStepIndex < stepIndex - 1 ) {
                                                                                      acc2++;
                                                                                  }

                                                                                  return acc2;
                                                                              },
                                                                              0
                                                                          );
                                                                      }

                                                                      return acc;
                                                                  },
                                                                  0
                                                              ),
                                                              targetTotalConnectionCount                 = targetPreviousTotalOutConnectionCount + targetSiblingSteps.reduce(
                                                                  ( inCount, s ) => inCount + ( s.connections?.in?.filter( ( inConnection ) => !targetPreviousStepKeys.includes( inConnection.step.key ) ).length ?? 0 ),
                                                                  0
                                                              ),
                                                              targetStepCardElBox                        = stepElementBoxes[ ( [] as BusinessProcessStep[] ).concat( businessProcess.steps[ targetStepIndex - 1 ] )?.[ 0 ]?.data.id ],
                                                              targetStartX                               = targetStepCardElBox ? targetStepCardElBox.left + targetStepCardElBox.width - offsetElBox.left : 0;

                                                          let targetVLaneNum = 0;

                                                          if ( isBackLink ) {
                                                              targetVLaneNum = targetTotalConnectionCount - targetPreviousSiblingsBackConnectionsCount - sourceIndex;
                                                          }
                                                          else {
                                                              const forwardLinks     = targetStep?.connections?.in.reduce(
                                                                        ( acc, inConnection ) => {
                                                                            const sourceStepIndex = businessProcess?.steps?.findIndex( s2 => ( [] as BusinessProcessStep[] ).concat( s2 ).find( bps => bps.data.key === inConnection.step.key ) ) ?? 0;

                                                                            if ( sourceStepIndex < targetStepIndex - 1 ) {
                                                                                acc.push( inConnection.step.key );
                                                                            }

                                                                            return acc;
                                                                        },
                                                                        [] as string[]
                                                                    ) ?? [],
                                                                    forwardLinkIndex = forwardLinks.findIndex( sKey => sKey === subStep.data.key );

                                                              targetVLaneNum = targetTotalConnectionCount - targetSiblingsBackConnections - targetNextSiblingsForwardConnectionsCount - forwardLinks.length + forwardLinkIndex + 1;
                                                          }

                                                          const targetVLaneX = targetStartX + arrowOffsetStart + targetVLaneNum * ( ( stepSpacing - arrowOffsetStart - arrowOffsetEnd - arrowWidth / 2 ) / ( targetTotalConnectionCount + 1 ) );

                                                          // backlink
                                                          if ( isBackLink ) {
                                                              const hLaneY = backLaneOffsetY - laneSpacing * ( bottomLanes.filter( hL => hL[ 0 ] <= subStep.data.order && hL[ 1 ] >= targetStep.data.order ).length );

                                                              bottomLanes.push( [ subStep.data.order, targetStep.data.order ] );

                                                              cornerRadiusToUse = cornerRadius;

                                                              line += ` L ${startVLaneX - cornerRadiusToUse} ${startY}
                                                            A ${cornerRadiusToUse} ${cornerRadiusToUse} 90 0 0 ${startVLaneX} ${startY - cornerRadiusToUse}
                                                            L ${startVLaneX} ${hLaneY + cornerRadiusToUse}
                                                            A ${cornerRadiusToUse} ${cornerRadiusToUse} 90 0 0 ${startVLaneX - cornerRadiusToUse} ${hLaneY}
                                                            L ${targetVLaneX + cornerRadiusToUse} ${hLaneY}
                                                            A ${cornerRadiusToUse} ${cornerRadiusToUse} 90 0 0 ${targetVLaneX} ${hLaneY + cornerRadiusToUse}
                                                            L ${targetVLaneX} ${targetY - cornerRadiusToUse}
                                                            A ${cornerRadiusToUse} ${cornerRadiusToUse} 90 0 0 ${targetVLaneX + cornerRadiusToUse} ${targetY}`;
                                                          }

                                                          // forward connection
                                                          else if ( isForwardConnection ) {
                                                              const hLaneY = forwardLaneOffsetY + laneSpacing * ( topLanes.filter( hL => hL[ 0 ] <= targetStep.data.order && hL[ 1 ] >= subStep.data.order ).length );

                                                              topLanes.push( [ subStep.data.order, targetStep.data.order ] );

                                                              cornerRadiusToUse = Math.min( cornerRadius, Math.abs( hLaneY - startY ) / 2 );

                                                              line += ` L ${startVLaneX - cornerRadiusToUse} ${startY}`;
                                                              if ( startY < hLaneY ) {
                                                                  line += ` A ${cornerRadiusToUse} ${cornerRadiusToUse} 90 0 1 ${startVLaneX} ${startY + cornerRadiusToUse}
                                                                    L ${startVLaneX} ${hLaneY - cornerRadiusToUse}
                                                                    A ${cornerRadiusToUse} ${cornerRadiusToUse} 90 0 0 ${startVLaneX + cornerRadiusToUse} ${hLaneY}`;
                                                              }
                                                              else {
                                                                  line += ` A ${cornerRadiusToUse} ${cornerRadiusToUse} 90 0 0 ${startVLaneX} ${startY - cornerRadiusToUse}
                                                                    L ${startVLaneX} ${hLaneY + cornerRadiusToUse}
                                                                    A ${cornerRadiusToUse} ${cornerRadiusToUse} 90 0 1 ${startVLaneX + cornerRadiusToUse} ${hLaneY}`;
                                                              }

                                                              cornerRadiusToUse = Math.min( cornerRadius, Math.abs( hLaneY - targetY ) / 2 );

                                                              line += ` L ${targetVLaneX - cornerRadiusToUse} ${hLaneY}`;
                                                              if ( hLaneY < targetY ) {
                                                                  line += ` A ${cornerRadiusToUse} ${cornerRadiusToUse} 90 0 1 ${targetVLaneX} ${hLaneY - cornerRadiusToUse}
                                                                    L ${targetVLaneX} ${targetY - cornerRadiusToUse}
                                                                    A ${cornerRadiusToUse} ${cornerRadiusToUse} 90 0 0 ${targetVLaneX + cornerRadiusToUse} ${targetY}`;
                                                              }
                                                              else {
                                                                  line += ` A ${cornerRadiusToUse} ${cornerRadiusToUse} 90 0 0 ${targetVLaneX} ${hLaneY - cornerRadiusToUse}
                                                                    L ${targetVLaneX} ${targetY + cornerRadiusToUse}
                                                                    A ${cornerRadiusToUse} ${cornerRadiusToUse} 90 0 1 ${targetVLaneX + cornerRadiusToUse} ${targetY}`;
                                                              }
                                                          }
                                                      }

                                                      // connection
                                                      else {
                                                          line += ` L ${startVLaneX - cornerRadiusToUse} ${startY}`;
                                                          if ( startY < targetY ) {
                                                              line += ` A ${cornerRadiusToUse} ${cornerRadiusToUse} 90 0 1 ${startVLaneX} ${startY + cornerRadiusToUse}
                                                                    L ${startVLaneX} ${targetY - cornerRadiusToUse}
                                                                    A ${cornerRadiusToUse} ${cornerRadiusToUse} 90 0 0 ${startVLaneX + cornerRadiusToUse} ${targetY}`;
                                                          }
                                                          else {
                                                              line += ` A ${cornerRadiusToUse} ${cornerRadiusToUse} 90 0 0 ${startVLaneX} ${startY - cornerRadiusToUse}
                                                                    L ${startVLaneX} ${targetY + cornerRadiusToUse}
                                                                    A ${cornerRadiusToUse} ${cornerRadiusToUse} 90 0 1 ${startVLaneX + cornerRadiusToUse} ${targetY}`;
                                                          }
                                                      }

                                                      line += ` L ${targetX} ${targetY}`;

                                                      retVal.push( {
                                                          id          : connection.id,
                                                          path        : line,
                                                          sourceStepId: subStep.data.id,
                                                          targetStepId: targetStep.data.id,
                                                          color       : connection.color,
                                                          label       : connection.label,
                                                          startX,
                                                          startY
                                                      } );
                                                  }
                                              }
                                          }
                                      }
                                  }
                              }
                          }
                      }

                      return retVal;
                  },
                  [
                      businessProcess,
                      businessProcessSteps,
                      language,
                      offsetElBox,
                      stepElementBoxes
                  ]
              ),
              selectedConnection                                                = useMemo(
                  () => selectedConnectionId ? connections.find( c => c.id === selectedConnectionId ) : undefined,
                  [
                      connections,
                      selectedConnectionId
                  ]
              ),
              conditionEditorDialog                                             = useMemo(
                  () => {
                      let retVal : React.ReactNode = null;

                      if ( showConditionEditorDialog && stepOfConditionToEdit ) {
                          retVal = <Dialog
                              key={`${uid}_conditionEditorDialog`}
                              id={`${uid}_conditionEditorDialog`}

                              visible={true}
                              onHide={onConditionEditorDialogHide}
                              focusOnShow={false}
                              closeOnEscape={false}
                              className="yn-businessprocess-dialog yn-businessprocess-condition-editor-dialog"
                              appendTo={() => {
                                  return document.querySelector( '.yn-businessprocess-wrapper' )! as HTMLElement;
                              }}
                              header={__( 'YN.base.frontend.businessProcessDesigner.conditionEditorDialog.title' )}
                              footer={<div>
                                  <Button
                                      id={`${uid}_okButton`}
                                      key="okButton"
                                      label={__( 'YN.base.common.ok' )}
                                      className="p-button-outlined mr-3"
                                      onClick={onConditionEditorDialogOkButtonClick}
                                  />
                                  <Button
                                      id={`${uid}_cancelButton`}
                                      key="cancelButton"
                                      label={__( 'YN.base.common.cancel' )}
                                      className="p-button-secondary p-button-outlined"
                                      onClick={onConditionEditorDialogHide}
                                  />
                              </div>}
                          >
                              {getConditionEditorFields()}
                          </Dialog>;
                      }

                      return retVal;
                  },
                  [
                      onConditionEditorDialogHide,
                      onConditionEditorDialogOkButtonClick,
                      getConditionEditorFields,
                      showConditionEditorDialog,
                      stepOfConditionToEdit,
                      uid
                  ]
              ),
              steps                                                             = useMemo(
                  () => {
                      const retVal : React.ReactNode[] = [];

                      if ( businessProcess?.steps ) {
                          initialRendered.current = true;

                          for ( let stepIndex = 0; stepIndex < businessProcess.steps.length; stepIndex++ ) {
                              const step                            = ( [] as BusinessProcessStep[] ).concat( businessProcess.steps[ stepIndex ] ),
                                    stepContent : React.ReactNode[] = [];

                              for ( let subStepIndex = 0; subStepIndex < step.length; subStepIndex++ ) {
                                  const subStep = step[ subStepIndex ];

                                  stepContent.push( getStepContent( subStep ) );
                              }

                              retVal.push(
                                  <div
                                      key={`businessProcess_step_${stepIndex}`}
                                      id={`${uid}_businessProcess_step_${stepIndex}`}
                                      className="yn-businessprocess-step"
                                  >
                                      {stepContent}
                                  </div>
                              );
                          }
                      }

                      retVal.push(
                          <div
                              key={`businessProcess_step_last`}
                              id={`${uid}_businessProcess_step_last`}
                              className="yn-businessprocess-step"
                              style={{ width: '1px' }}
                          >
                              &nbsp;
                          </div>
                      );

                      return retVal;
                  },
                  [
                      businessProcess,
                      getStepContent,
                      messageCatalogTimestamp,
                      metaModelTimestamp,
                      language
                  ]
              ),
              connectionDeleteButton                                            = useMemo(
                  () => {
                      /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
                      return <div
                          key={`${uid}_connectionDeleteButton`}
                          className="yn-businessprocess-connection-delete pi pi-minus-circle"
                          style={{
                              top    : selectedConnection?.startY ?? 0,
                              left   : selectedConnection?.startX ?? 0,
                              display: selectedConnection ? 'block' : 'none'
                          }}
                          onClick={selectedConnection ? onDeleteConnectionClick : undefined}
                      ></div>;
                  },
                  [
                      onDeleteConnectionClick,
                      selectedConnection,
                      uid
                  ]
              ),
              content                                                           = useMemo(
                  () => {
                      return <div
                          key={`businessProcess`}
                          id={`${uid}_businessProcess`}
                          className="yn-businessprocess"
                      >
                          {conditionEditorDialog}
                          {connectionDeleteButton}
                          {steps}
                      </div>;
                  },
                  [
                      conditionEditorDialog,
                      connectionDeleteButton,
                      steps
                  ]
              );

        console.log( 'dataDescriptors', dataDescriptors );

        currentScrollTop.current = scrollTop;
        currentScrollLeft.current = scrollLeft;
        cBPS.current = businessProcess?.steps?.map( s => ( [] as BusinessProcessStep[] ).concat( s ) ) ?? [];
        cSEB.current = stepElementBoxes;

        onBackgroundClickProps.current = {
            configRootItemId,
            onSelectStepOrConnection,
            uid
        };

        // expose methods via reference
        useImperativeHandle(
            forwardedRef,
            () => ( {
                dataStructure: businessProcess?.dataStructure,
                addStep      : onAddStep
            } )
        );

        // ( on mount / unmount )
        // add/remove listener for color scheme switch
        // remove the observed element from resizeObserver
        useEffect(
            () => {
                const matchMediaChangeHandler = () => {
                    setColorScheme( getColorScheme() );
                };

                matchMedia( '(prefers-color-scheme: dark)' ).addEventListener( 'change', matchMediaChangeHandler );

                document.getElementById( `${uid}_businessProcess` )?.parentElement?.addEventListener( 'click', onBackgroundClick );
                document.getElementById( `${uid}_businessProcess` )?.parentElement?.addEventListener( 'scroll', debouncedGetScrollPosition );

                // unmount
                return () => {
                    matchMedia( '(prefers-color-scheme: dark)' ).removeEventListener( 'change', matchMediaChangeHandler );

                    document.getElementById( `${uid}_businessProcess` )?.parentElement?.removeEventListener( 'click', onBackgroundClick );
                    document.getElementById( `${uid}_businessProcess` )?.parentElement?.removeEventListener( 'scroll', debouncedGetScrollPosition );
                }
            },
            []
        );

        // ( on update )
        // load config and data if / graphHeight and graphWidth / initialize graph
        useEffect(
            () => {
                const wrapperEl = document.getElementById( `${uid}_businessProcess` );

                // update graphHeight and graphWidth
                if ( wrapperEl ) {
                    if ( graphWidth !== wrapperEl.scrollWidth ) {
                        setGraphWidth( wrapperEl.scrollWidth );
                    }
                    if ( graphHeight !== wrapperEl.scrollHeight ) {
                        setGraphHeight( wrapperEl.scrollHeight + 100 );
                    }

                    // initialize graph
                    if ( !graphSvg.current ) {
                        hasRendered.current = true;

                        const globalStyle          = window.getComputedStyle( document.documentElement ),
                              svgLinkColor         = globalStyle.getPropertyValue( '--businessprocess-connection-color' ),
                              svgLinkHoverColor    = globalStyle.getPropertyValue( '--businessprocess-connection-hover-color' ),
                              svgLinkDragColor     = globalStyle.getPropertyValue( '--businessprocess-connection-drag-color' ),
                              svgLinkSelectedColor = globalStyle.getPropertyValue( '--businessprocess-connection-selected-color' ),
                              svgDropShadowColor   = globalStyle.getPropertyValue( '--businessprocess-drop-shadow-color' );

                        const svg = d3.select( wrapperEl )
                            .append( 'svg' )
                            .attr( 'id', `${uid}_svg` )
                            .style( 'font', globalStyle.getPropertyValue( 'font' ) )
                            .attr( 'viewBox', [ 0, 0, wrapperEl.clientWidth, wrapperEl.clientHeight ] )
                            .attr( 'width', wrapperEl.clientWidth )
                            .attr( 'height', wrapperEl.clientHeight )
                            .append( 'g' );

                        // add shadow filters
                        const filters = svg.append( 'filter' )
                            .attr( 'id', shadowFilterId )
                            .attr( 'height', '400%' )
                            .attr( 'width', '400%' )
                            .attr( 'x', '-150%' )
                            .attr( 'y', '-150%' );

                        filters.append( 'feGaussianBlur' )
                            .attr( 'in', 'BackgroundImage' )
                            .attr( 'stdDeviation', '2' )
                            .attr( 'result', 'penumbraBlur' );

                        filters.append( 'feOffset' )
                            .attr( 'in', 'penumbraBlur' )
                            .attr( 'dx', '1' )
                            .attr( 'dy', '2' )
                            .attr( 'result', 'penumbraBlurOffset' );

                        filters.append( 'feComponentTransfer' )
                            .attr( 'in', 'penumbraBlurOffset' )
                            .append( 'feFuncA' )
                            .attr( 'type', 'linear' )
                            .attr( 'slope', '1' )//'0.37' )
                            .attr( 'result', 'penumbra' );

                        filters.append( 'feMerge' )
                            .append( 'feMergeNode' )
                            .attr( 'in', 'penumbra' );

                        filters.append( 'feMergeNode' )
                            .attr( 'in', 'SourceGraphic' );

                        // add marker defs (arrow head)
                        const defs = svg.append( 'defs' )
                            .attr( 'id', `${uid}_defs` );

                        defs.append( 'marker' )
                            .attr( 'id', arrowMarkerId )
                            .attr( 'viewBox', '0 -4 12 8' )
                            .attr( 'refX', 5 )
                            .attr( 'refY', 0 )
                            .attr( 'markerWidth', 6 )
                            .attr( 'markerHeight', 4 )
                            .attr( 'orient', 'auto-start-reverse' )
                            .append( 'path' )
                            .attr( 'fill', svgLinkColor )
                            .attr( 'd', 'M 0 -4 L 12 0 L 0 4 z' );

                        defs.select( `#${arrowMarkerId}` )
                            .clone( true )
                            .attr( 'id', `${arrowMarkerId}_hover` )
                            .select( 'path' )
                            .attr( 'fill', svgLinkHoverColor );

                        defs.select( `#${arrowMarkerId}` )
                            .clone( true )
                            .attr( 'id', `${arrowMarkerId}_shadow` )
                            .select( 'path' )
                            .attr( 'fill', svgDropShadowColor );

                        defs.select( `#${arrowMarkerId}` )
                            .clone( true )
                            .attr( 'id', `${arrowMarkerId}_drag` )
                            .select( 'path' )
                            .attr( 'fill', svgLinkDragColor );

                        defs.select( `#${arrowMarkerId}` )
                            .clone( true )
                            .attr( 'id', `${arrowMarkerId}_selected` )
                            .select( 'path' )
                            .attr( 'fill', svgLinkSelectedColor );

                        graphSvg.current = svg.append( 'g' );
                    }
                }
            }
        );

        // update step positions
        useEffect(
            () => {
                if ( businessProcess?.steps ) {
                    const offsetEl                                            = document.getElementById( `${uid}_businessProcess` ),
                          newStepElementBoxes : { [ id : number ] : DOMRect } = {};

                    for ( let stepIndex = 0; stepIndex < businessProcess.steps.length; stepIndex++ ) {
                        const subSteps : BusinessProcessStep[] = ( [] as BusinessProcessStep[] ).concat( businessProcess.steps[ stepIndex ] );

                        for ( let subStepIndex = 0; subStepIndex < subSteps.length; subStepIndex++ ) {
                            const subStepId     = subSteps[ subStepIndex ].data.id,
                                  subStepCardEl = document.getElementById( `${uid}_businessProcessStep_${subStepId}` );

                            if ( subStepCardEl ) {
                                newStepElementBoxes[ subStepId ] = subStepCardEl.getBoundingClientRect();
                            }
                        }
                    }

                    if ( JSON.stringify( offsetElBox ) !== JSON.stringify( offsetEl?.getBoundingClientRect() ) ) {
                        setOffsetElBox( offsetEl?.getBoundingClientRect() );
                    }
                    if ( JSON.stringify( stepElementBoxes ) !== JSON.stringify( newStepElementBoxes ) ) {
                        setStepElementBoxes( newStepElementBoxes );
                    }
                }
            },
            [
                businessProcess,
                content,
                uid
            ]
        );

        // update height and width
        useEffect(
            () => {
                if ( graphSvg.current ) {
                    d3.select( `#${uid}_svg` )
                        .attr( 'viewBox', [ 0, 0, graphWidth, graphHeight ] )
                        .attr( 'height', graphHeight )
                        .attr( 'width', graphWidth );
                }
            },
            [
                graphHeight,
                graphWidth,
                graphSvg.current
            ]
        );

        // update colorScheme dependent settings
        useEffect(
            () => {
                const handler = () => {
                    const globalStyle          = window.getComputedStyle( document.documentElement ),
                          svgBackgroundColor   = globalStyle.getPropertyValue( '--businessprocess-background-color' ),
                          svgLinkColor         = globalStyle.getPropertyValue( '--businessprocess-connection-color' ),
                          svgLinkHoverColor    = globalStyle.getPropertyValue( '--businessprocess-connection-hover-color' ),
                          svgLinkDragColor     = globalStyle.getPropertyValue( '--businessprocess-connection-drag-color' ),
                          svgLinkSelectedColor = globalStyle.getPropertyValue( '--businessprocess-connection-selected-color' ),
                          svgDropShadowColor   = globalStyle.getPropertyValue( '--businessprocess-drop-shadow-color' );

                    let brightness : number | undefined;

                    if ( svgBackgroundColor ) {
                        const rgb = svgBackgroundColor.length === 4 || svgBackgroundColor.length === 5
                                  ? /^#?([a-f\d]{1})([a-f\d]{1})([a-f\d]{1})$/i.exec( svgBackgroundColor )
                                  : /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec( svgBackgroundColor ),
                              r   = parseInt( rgb?.[ 1 ] ?? 'ff', 16 ),
                              g   = parseInt( rgb?.[ 2 ] ?? 'ff', 16 ),
                              b   = parseInt( rgb?.[ 3 ] ?? 'ff', 16 );

                        brightness = rgb
                            ? Math.round( (
                                ( r * 299 ) +
                                ( g * 587 ) +
                                ( b * 114 )
                            ) / 1000 )
                            : 255;
                    }

                    if (
                        brightness === undefined
                        || ( colorScheme === 'dark' && brightness > 50 )
                        || ( colorScheme === 'light' && brightness < 200 )
                    ) {
                        window.setTimeout(
                            handler,
                            10
                        );
                    }
                    else {
                        // defs
                        d3.select( `#${arrowMarkerId}` )
                            .select( 'path' )
                            .attr( 'fill', svgLinkColor );
                        d3.select( `#${arrowMarkerId}_hover` )
                            .select( 'path' )
                            .attr( 'fill', svgLinkHoverColor );
                        d3.select( `#${arrowMarkerId}_shadow` )
                            .select( 'path' )
                            .attr( 'fill', svgDropShadowColor );
                        d3.select( `#${arrowMarkerId}_drag` )
                            .select( 'path' )
                            .attr( 'fill', svgLinkDragColor );
                        d3.select( `#${arrowMarkerId}_selected` )
                            .select( 'path' )
                            .attr( 'fill', svgLinkSelectedColor );

                        // links
                        graphSvg.current
                            ?.selectAll( 'path' )
                            .filter( ( d, i, nodeList ) => ( nodeList[ i ] as SVGElement | undefined )?.id.indexOf( '_path' ) !== -1 )
                            .attr( 'stroke', d => {
                                const connection = d as Connection;

                                let retVal = svgLinkColor;

                                if ( selectedConnectionId === connection.id ) {
                                    retVal = svgLinkSelectedColor;
                                }
                                else if ( connection.color ) {
                                    retVal = connection.color;
                                }

                                return retVal;
                            } );
                        graphSvg.current
                            ?.selectAll( 'path' )
                            .filter( ( d, i, nodeList ) => ( nodeList[ i ] as SVGElement | undefined )?.id.indexOf( '_background' ) !== -1 )
                            .attr( 'stroke', svgBackgroundColor );
                        graphSvg.current
                            ?.selectAll( 'path' )
                            .filter( ( d, i, nodeList ) => ( nodeList[ i ] as SVGElement | undefined )?.id.indexOf( '_shadowPath' ) !== -1 )
                            .attr( 'stroke', svgDropShadowColor );
                    }
                };

                window.setTimeout(
                    handler,
                    10
                );
            },
            [
                colorScheme
            ]
        );

        // draw connections
        useLayoutEffect(
            () => {
                // clear any current
                graphSvg.current
                    ?.selectAll( 'g' )
                    .remove();

                if ( connections?.length ) {
                    const globalStyle          = window.getComputedStyle( document.documentElement ),
                          svgBackgroundColor   = globalStyle.getPropertyValue( '--businessprocess-background-color' ),
                          svgLinkColor         = globalStyle.getPropertyValue( '--businessprocess-connection-color' ),
                          svgLinkDragColor     = globalStyle.getPropertyValue( '--businessprocess-connection-drag-color' ),
                          svgLinkSelectedColor = globalStyle.getPropertyValue( '--businessprocess-connection-selected-color' );

                    for ( const connection of connections ) {
                        if ( connection.color ) {
                            const markerId = `${arrowMarkerId}_${connection.color.substring( 1 )}`,
                                  marker   = document.getElementById( `#${markerId}` );

                            if ( !marker ) {
                                d3.select( `#${uid}_defs` )
                                    .select( `#${arrowMarkerId}` )
                                    .clone( true )
                                    .attr( 'id', markerId )
                                    .select( 'path' )
                                    .attr( 'fill', connection.color );
                            }
                        }
                    }

                    graphSvg.current
                        ?.selectAll( 'g' )
                        .data( connections )
                        .join( 'g' )
                        .attr( 'id', d => `${uid}_${d.id}` )
                        .attr( 'class', d => `yn-businessprocess-connection${selectedConnectionId === d.id ? ' yn-selected' : ''}` )
                        .lower()
                        .append( 'path' )
                        .attr( 'id', d => `${uid}_${d.id}_path` )
                        .attr( 'd', d => d.path )
                        .attr( 'class', 'yn-businessprocess-connection-path' )
                        .attr( 'stroke', d => {
                            let retVal = svgLinkColor;

                            if ( dragInfo.current?.referenceId === d.id ) {
                                retVal = svgLinkDragColor;
                            }
                            else if ( selectedConnectionId === d.id ) {
                                retVal = svgLinkSelectedColor;
                            }
                            else if ( d.color ) {
                                retVal = d.color;
                            }

                            return retVal;
                        } )
                        .attr( 'marker-end', d => {
                            let markerId = arrowMarkerId;

                            if ( dragInfo.current?.referenceId === d.id ) {
                                markerId = `${arrowMarkerId}_drag`;
                            }
                            else if ( selectedConnectionId === d.id ) {
                                markerId = `${arrowMarkerId}_selected`;
                            }
                            else if ( d.color ) {
                                markerId = `${arrowMarkerId}_${d.color.substring( 1 )}`;
                            }

                            return `url(#${markerId})`;
                        } )
                        .on( 'click', onConnectionClick )
                        .on( 'mouseover', onConnectionOver )
                        .on( 'mouseout', onConnectionOut )
                        .clone( true )
                        .lower()
                        .attr( 'id', d => `${uid}_${d.id}_background` )
                        .attr( 'class', 'yn-businessprocess-connection-background' )
                        .attr( 'stroke', svgBackgroundColor )
                        .attr( 'marker-end', `none` )
                        .on( 'click', onConnectionClick )
                        .on( 'mouseover', onConnectionOver )
                        .on( 'mouseout', onConnectionOut );
                }
            },
            [
                arrowMarkerId,
                connections,
                onConnectionClick,
                onConnectionOut,
                onConnectionOver,
                selectedConnectionId,
                uid
            ]
        );

        // set the scroll position (on initial render or change)
        useLayoutEffect(
            () => {
                setScrollPosition();
            },
            [
                scrollLeft,
                scrollTop,
                initialRendered.current
            ]
        );

        useLayoutEffect(
            () => {
                document.getElementById( `${uid}_businessProcessStep_${selectedStepId}` )?.scrollIntoView( { behavior: 'smooth', block: 'nearest' } );
            },
            [
                selectedStepId,
                uid
            ]
        );

        if ( debug ) {
            console.log( `%crender - ${Math.round( 1000 * ( performance.now() - ( startTime + additionalRenderTime.current ) ) ) / 1000} ms%c`.asLogLine( 104 ), `color: #${debugColorScheme.render}; font-weight: bold;`, 'font-weight: unset;' );
            additionalRenderTime.current = 0;
        }

        return content;
    } )
;
BusinessProcessDesigner.defaultProps = defaultProps;
BusinessProcessDesigner.displayName = 'BusinessProcessDesigner';

export default BusinessProcessDesigner;
